import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import 'katex/dist/katex.min.css';
import './App.css';

// Import Capacitor initialization for mobile app support
import initializeCapacitor, { cleanupCapacitor } from './capacitor';

// Import Authentication Context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ConfirmProvider } from './contexts/ConfirmContext';

// Import Page Components
import LandingPage from './components/landing/LandingPage';
import AuthPage from './components/auth/AuthPage';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import VerifyEmailPage from './components/auth/VerifyEmailPage';

// Import Common Components
import ProtectedRoute from './components/common/ProtectedRoute';
import ContextSwitcher from './components/common/ContextSwitcher';
import BottomNavigation from './components/common/BottomNavigation';
import StudySupportSection from './components/common/StudySupportSection';
import TaskSelectionModal from './components/common/TaskSelectionModal';
import MyDocsManagerModal from './components/common/MyDocsManagerModal';
import ViewSyllabusModal from './components/common/ViewSyllabusModal';
import KaTeXContent from './components/common/KaTeXContent';
import RoleBasedRedirect from './components/common/RoleBasedRedirect';
import FeedbackModal from './components/common/FeedbackModal';
import AIChatAssistant from './components/common/AIChatAssistant';
import RefreshActionButton from './components/common/RefreshActionButton';
import {formatTopics,
  normalizeGrade,
  formatGradeLabel,
  truncateTaskTitle,
  formatTaskDueDateTime,
  isTaskOverdue,
  isTaskSubmittedLate,
} from './utils/formatters';
import { buildExplainPrompt } from './utils/aiChatExplain';
import edsuppLogoBoy from './assets/logo/edsupp-logo-boy.svg';
import edsuppLogoGirl from './assets/logo/edsupp-logo-girl.svg';

// Global impersonation banner (eager: rendered on every authenticated screen)
import ImpersonationBanner from './components/admin/ImpersonationBanner';

// Eager imports: named exports, small/common components, and plain utilities.
import SkillWiseScoreChart from './components/evaluation/SkillWiseScoreChart';
import FullAnalysisModal from './components/common/FullAnalysisModal';
import CompareAnswersModal from './components/common/CompareAnswersModal';
import { AddBatchModal } from './components/coaching/BatchManagement';
import { getFirstName } from './utils/formatters';
import { printWorksheet, saveWorksheetAsPDF } from './utils/worksheetExport';
import { getUserWebSocketUrl, parseWebSocketMessage } from './utils/websocket';
import useWorksheetData from './hooks/useWorksheetData';
import { cachedFetch, queryCache } from './utils/queryCache';
import { EditChildModal, DeleteConfirmModal, AddChildModal } from './components/parent/ChildManagement';
import ConfirmModal from './components/common/ConfirmModal';

// ---------------------------------------------------------------------------
// Code-splitting: the route/role dashboards, heavy modals and large feature
// panels are loaded with React.lazy so they no longer ship in the initial
// bundle. They render under the top-level <Suspense> boundary in this file, so
// behavior is unchanged — a brief fallback shows while a chunk loads on first
// use. (Phase 5 of the architecture refactor.)
// ---------------------------------------------------------------------------
const SuperAdminDashboard = lazy(() => import('./components/admin/SuperAdminDashboard'));
const Dashboard = lazy(() => import('./components/parent/Dashboard'));
const ChildInsightsModal = lazy(() => import('./components/parent/ChildInsightsModal'));
const TeacherDashboard = lazy(() => import('./components/teacher/TeacherDashboard'));
const ChildDashboard = lazy(() => import('./components/child/ChildDashboard'));
const QuestionBankDashboard = lazy(() => import('./components/qb/QuestionBankDashboard'));
const SubscriptionScreen = lazy(() => import('./components/billing/SubscriptionScreen'));
const FeatureGateModal = lazy(() => import('./components/billing/FeatureGateModal'));
const AdminBillingDashboard = lazy(() => import('./components/billing/AdminBillingDashboard'));
const EnhancedWorksheetDisplay = lazy(() => import('./components/worksheet/EnhancedWorksheetDisplay'));
const WorksheetGenerator = lazy(() => import('./components/worksheet/WorksheetGenerator'));
const WorksheetViewer = lazy(() => import('./components/worksheet/WorksheetViewer'));
const VideoCreatorModal = lazy(() => import('./components/video/VideoCreatorModal'));
const NotesGenerator = lazy(() => import('./components/notes/NotesGenerator'));
const NotesAssignmentView = lazy(() => import('./components/notes/NotesAssignmentView'));
const ShareNotesModal = lazy(() => import('./components/notes/ShareNotesModal'));
const EditEvaluationModal = lazy(() => import('./components/evaluation/EditEvaluationModal'));
const PersonalizedWorksheetModal = lazy(() => import('./components/evaluation/PersonalizedWorksheetModal'));
const EditWorksheetModal = lazy(() => import('./components/worksheet/EditWorksheetModal'));
const CoachingTasksSection = lazy(() => import('./components/coaching/CoachingTasksSection'));
const CoachingStudentsSection = lazy(() => import('./components/coaching/CoachingStudentsSection'));
const CoachingChildView = lazy(() => import('./components/coaching/CoachingChildView'));
const GuestAssignmentPage = lazy(() => import('./components/guest/GuestAssignmentPage'));
const SchoolAdminDashboard = lazy(() => import('./components/school/SchoolAdminDashboard'));
const SubmitWorkModal = lazy(() => import('./components/submission/SubmitWorkModal'));
const OlympiadModal = lazy(() => import('./components/olympiad/OlympiadModal'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.baseURL = API;

// Public coach micro-site page — fetches /api/public/coach/<slug> and
// renders the profile in the SPA. Avoids the redirect-loop pitfall that
// hit deployments where FRONTEND_URL and BACKEND_URL resolve to the
// same host (window.location.replace would re-enter the SPA).
const PublicCoachPage = lazy(() => import('./components/coaching/PublicCoachPage'));

// Set auth token if available
const token = localStorage.getItem('edsupp_token');
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Dashboard Component
// Coaching Child View Component (shows in parent's coaching space)

// Coach Dashboard Component
const CoachDashboard = () => {
  const { user, logout } = useAuth();
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [showAddBatchModal, setShowAddBatchModal] = useState(false);
  const [addStudentForm, setAddStudentForm] = useState({
    parent_email: '',
    child_name: '',
    child_grade: '',
    subject: '',
    message: ''
  });
  const [addBatchForm, setAddBatchForm] = useState({
    name: '',
    subject: '',
    grade: '',
    location: ''
  });
  const coachLogos = [edsuppLogoBoy, edsuppLogoGirl];
  const [activeLogoIndex, setActiveLogoIndex] = useState(0);

  const loadStudents = async () => {
    try {
      const response = await axios.get('/coaching/relationships');
      setStudents(response.data.relationships);
    } catch (error) {
      console.error('Error loading students:', error);
    }
  };

  const loadBatches = async () => {
    try {
      const response = await axios.get('/batches');
      setBatches(response.data.batches);
    } catch (error) {
      console.error('Error loading batches:', error);
    }
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/coaching/request', addStudentForm);
      alert('Coaching request sent successfully! The parent will receive a notification.');
      setShowAddStudentModal(false);
      setAddStudentForm({
        parent_email: '',
        child_name: '',
        child_grade: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error sending request:', error);
      alert(error.response?.data?.detail || 'Error sending request');
    }
  };

  const handleAddBatch = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/batches', addBatchForm);
      alert('Batch created successfully!');
      setShowAddBatchModal(false);
      setAddBatchForm({
        name: '',
        subject: '',
        grade: '',
        location: ''
      });
      loadBatches(); // Reload batches list
    } catch (error) {
      console.error('Error creating batch:', error);
      alert(error.response?.data?.detail || 'Error creating batch');
    }
  };

  useEffect(() => {
    loadStudents();
    loadBatches();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveLogoIndex((prevIndex) => (prevIndex + 1) % coachLogos.length);
    }, 2500);

    return () => clearInterval(interval);
  }, [coachLogos.length]);

  const activeLogo = coachLogos[activeLogoIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <img 
                src={activeLogo} 
                alt="Edsupp Logo" 
                className="h-8 w-8 rounded bg-white/90 p-1"
              />
              <h1 className="text-xl font-bold text-blue-900">Edsupp</h1>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Hi, {(user.name || '').trim().split(/\s+/)[0]}</span>
              <button
                onClick={logout}
                className="text-gray-600 hover:text-gray-900"
              >
                ⏻
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard Header */}
        <div className="mb-2 sm:mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Coaching Dashboard</h1>
          <p className="text-gray-600">Manage your students and track their progress</p>
        </div>

        {/* Add Student Button */}
        <div className="mb-2 sm:mb-4">
          <button
            onClick={() => setShowAddStudentModal(true)}
            className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors flex items-center"
          >
            <span className="mr-2">👨‍🎓</span>
            Add New Student
          </button>
        </div>

        {/* Students and Batches Grid */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Students List */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2 sm:mb-4">My Students ({students.length})</h2>
            
            {students.length > 0 ? (
              <div className="space-y-4">
                {students.map(student => (
                  <div key={student.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                          <span className="text-lg font-medium text-purple-600">
                            {student.child_details?.name?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {student.child_details?.name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            Grade {student.child_details?.grade} | Parent: {student.parent_details?.name}
                          </p>
                          {student.subject && (
                            <p className="text-sm text-gray-600">
                              Subject: {student.subject}
                            </p>
                          )}
                          <p className="text-xs text-gray-500">
                            Since {new Date(student.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                        View Progress
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2 sm:mb-4">👨‍🎓</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Students Yet</h3>
                <p className="text-sm">
                  Start by adding students using the "Add New Student" button above.
                  Parents will receive a request to approve your coaching.
                </p>
              </div>
            )}
          </div>

          {/* Batches List */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-2 sm:mb-4">
              <h2 className="text-lg font-semibold text-gray-900">My Batches ({batches.length})</h2>
              <button
                onClick={() => setShowAddBatchModal(true)}
                className="text-purple-600 hover:text-purple-800 text-sm font-medium"
              >
                + Add Batch
              </button>
            </div>
            
            {batches.length > 0 ? (
              <div className="space-y-4">
                {batches.map(batch => (
                  <div key={batch.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                          <span className="text-lg font-medium text-blue-600">
                            {batch.subject?.charAt(0)?.toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">
                            {batch.name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {batch.subject} | Grade {batch.grade}
                          </p>
                          {batch.location && (
                            <p className="text-sm text-gray-600">
                              📍 {batch.location}
                            </p>
                          )}
                          <p className="text-xs text-gray-500">
                            Created: {new Date(batch.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                        Manage
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-2 sm:mb-4">📚</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Batches Yet</h3>
                <p className="text-sm">
                  Create batches to organize your students by subject, grade, or location.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Add Student Modal */}
        {showAddStudentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900">Add New Student</h2>
                  <button
                    onClick={() => setShowAddStudentModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <form onSubmit={handleAddStudent} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parent Email *</label>
                  <input
                    type="email"
                    required
                    value={addStudentForm.parent_email}
                    onChange={(e) => setAddStudentForm({...addStudentForm, parent_email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="parent@example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Child Name *</label>
                  <input
                    type="text"
                    required
                    value={addStudentForm.child_name}
                    onChange={(e) => setAddStudentForm({...addStudentForm, child_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="Student's name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
                  <select
                    value={addStudentForm.child_grade}
                    onChange={(e) => setAddStudentForm({...addStudentForm, child_grade: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select grade</option>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(grade => (
                      <option key={grade} value={grade}>{grade}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                  <input
                    type="text"
                    value={addStudentForm.subject}
                    onChange={(e) => setAddStudentForm({...addStudentForm, subject: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    placeholder="e.g., Mathematics, Science"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                  <textarea
                    value={addStudentForm.message}
                    onChange={(e) => setAddStudentForm({...addStudentForm, message: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    rows={3}
                    placeholder="Introduce yourself and explain how you can help..."
                  />
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddStudentModal(false)}
                    className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-2 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    Send Request
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Batch Modal */}
        {showAddBatchModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-gray-900">Create New Batch</h2>
                  <button
                    onClick={() => setShowAddBatchModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
              <form onSubmit={handleAddBatch} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1"><strong>Batch Name *</strong></label>
                  <input
                    type="text"
                    required
                    value={addBatchForm.name}
                    onChange={(e) => setAddBatchForm({...addBatchForm, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Morning Mathematics"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                  <input
                    type="text"
                    required
                    value={addBatchForm.subject}
                    onChange={(e) => setAddBatchForm({...addBatchForm, subject: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Mathematics, Science"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grade *</label>
                  <select
                    required
                    value={addBatchForm.grade}
                    onChange={(e) => setAddBatchForm({...addBatchForm, grade: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select grade</option>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(grade => (
                      <option key={grade} value={grade}>{grade}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    type="text"
                    value={addBatchForm.location}
                    onChange={(e) => setAddBatchForm({...addBatchForm, location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Room 101, Online"
                  />
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddBatchModal(false)}
                    className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Create Batch
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Parent Access Code Modal Component (for switching from child to parent view)
const ParentAccessCodeModal = ({ onClose, onSuccess }) => {
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Verify parent access code with backend
      const result = await axios.post('/auth/verify-access-code', {
        access_code: accessCode
      });
      
      if (result.data.valid && result.data.redirect_to === 'parent') {
        // Store parent dashboard info and switch to parent view
        localStorage.setItem('dashboardType', 'parent');
        localStorage.setItem('activeUserId', result.data.user_id);
        localStorage.removeItem('dashboardMode');
        localStorage.removeItem('activeChildData'); // Clear any legacy child PII blob
        onSuccess();
      } else {
        setError('Invalid parent access code');
      }
    } catch (error) {
      setError('Invalid parent access code');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-2 sm:mb-4">
            <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Enter Parent Access Code</h3>
          <p className="text-sm text-gray-600 mb-2 sm:mb-4">
            Please enter your 6-digit parent access code to switch to Parent View
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                required
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter 6-digit access code"
                pattern="[0-9]{6}"
                minLength="6"
                maxLength="6"
                data-testid="parent-access-code-input"
              />
            </div>
            
            {error && (
              <div className="text-red-600 text-sm" data-testid="access-code-error">
                {error}
              </div>
            )}
            
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || accessCode.length !== 6 || !/^\d{6}$/.test(accessCode)}
                className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                data-testid="verify-access-code-btn"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Child Access Code Modal Component (for switching between children in child view)
const ChildAccessCodeModal = ({ onClose, onSuccess, targetChild }) => {
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await axios.post('/auth/verify-access-code', {
        access_code: accessCode
      });

      if (
        result.data.valid &&
        result.data.redirect_to === 'child' &&
        result.data.user_id === targetChild?.id
      ) {
        onSuccess();
      } else {
        setError(`Access code does not match ${targetChild?.name || 'this child'}`);
      }
    } catch (error) {
      setError('Invalid child access code');
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 mb-2 sm:mb-4">
            <svg className="h-6 w-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 14c-2.761 0-5 1.239-5 2.769V19h10v-2.231C17 15.239 14.761 14 12 14z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Enter Child Access Code</h3>
          <p className="text-sm text-gray-600 mb-2 sm:mb-4">
            Please enter the 4-digit access code for {targetChild?.name || 'this child'} to continue.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="password"
                required
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Enter 4-digit access code"
                pattern="[0-9]{4}"
                minLength="4"
                maxLength="4"
                data-testid="child-access-code-input"
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm" data-testid="child-access-code-error">
                {error}
              </div>
            )}

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || accessCode.length !== 4 || !/^\d{4}$/.test(accessCode)}
                className="flex-1 py-2 px-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                data-testid="verify-child-access-code-btn"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Parent Dashboard Component
const ParentDashboard = ({ 
  user, children, activeChild, setActiveChild, showAddChild, setShowAddChild, 
  showWorksheetGenerator, setShowWorksheetGenerator, showNotesGenerator, setShowNotesGenerator,
  setWorksheetGeneratorInitialConfig,
  notesGeneratorInitialConfig, setNotesGeneratorInitialConfig,
  setShowVideoCreator,
  showViewSyllabus, setShowViewSyllabus, showSubmitWork, setShowSubmitWork,
  showStudySupportModal, setShowStudySupportModal,
  selectedSubject, setSelectedSubject, childSubjects, setChildSubjects,
  activeView: activeViewProp, setActiveView: setActiveViewProp,
  selectedGrade: selectedGradeProp, setSelectedGrade: setSelectedGradeProp,
  currentPage, onChildAdded, coachingRequests, coachingRelationships,
  showCoachingRequests, setShowCoachingRequests, showCoachingSpace, setShowCoachingSpace,
  handleCoachingRequest, olympiadWorksheet = null, showOlympiadModal, setShowOlympiadModal,
  showArchivedTasks, setShowArchivedTasks, loadArchivedTasks, taskRefreshKey = 0,
  openChildInsightsPage,
  openTaskSelectionPage
}) => {
  const [pendingTasks, setPendingTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [loadingChildData, setLoadingChildData] = useState(false);
  const [showEditChild, setShowEditChild] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReassignConfirm, setShowReassignConfirm] = useState(false);
  const [pendingReassignTaskId, setPendingReassignTaskId] = useState(null);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [pendingArchiveTaskId, setPendingArchiveTaskId] = useState(null);
  const [showDeleteTaskConfirm, setShowDeleteTaskConfirm] = useState(false);
  const [pendingDeleteTask, setPendingDeleteTask] = useState(null);
  const [selectedTaskForUpload, setSelectedTaskForUpload] = useState(null);
  const [deletingChild, setDeletingChild] = useState(null);
  const [formData, setFormData] = useState({ mode: 'practice' });
  const [showTaskSelection, setShowTaskSelection] = useState(false);
  const [showMyDocs, setShowMyDocs] = useState(false);
  const [showChildInsights, setShowChildInsights] = useState(false);
  const [childPerformance, setChildPerformance] = useState(null);
  const [childEvaluations, setChildEvaluations] = useState({});
  const [showParentEvaluationModal, setShowParentEvaluationModal] = useState(false);
  const [currentParentEvaluation, setCurrentParentEvaluation] = useState(null);
  const [showAnswerKeyModal, setShowAnswerKeyModal] = useState(false);
  const [currentAnswerKey, setCurrentAnswerKey] = useState(null);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [currentComparison, setCurrentComparison] = useState(null);
  const [aiChatRequest, setAiChatRequest] = useState(null);
  const {
    currentPracticeSheet,
    showPracticeSheetModal,
    showPracticeSheetAnswerKey,
    openPracticeSheet,
    closePracticeSheet,
    togglePracticeSheetAnswerKey,
    updateCurrentPracticeSheetWorksheet,
  } = useWorksheetData();
  
  // New Feature Modal States
  const [showEditEvaluationModal, setShowEditEvaluationModal] = useState(false);
  const [showPersonalizedWorksheetModal, setShowPersonalizedWorksheetModal] = useState(false);
  
  // Coaching Dashboard States
  // activeView can be controlled by the parent (Dashboard.jsx sidebar) or managed locally.
  const [activeViewInternal, setActiveViewInternal] = useState('parent'); // 'parent', 'school', or coach relationship ID
  const activeView = activeViewProp !== undefined ? activeViewProp : activeViewInternal;
  const setActiveView = setActiveViewProp || setActiveViewInternal;
  const [coachingTasks, setCoachingTasks] = useState({ pending: [], completed: [] });
  const [coachingEvaluations, setCoachingEvaluations] = useState({});
  const [schoolTasks, setSchoolTasks] = useState({ pending: [], completed: [] });
  const [schoolEvaluations, setSchoolEvaluations] = useState({});
  
  // Task display control
  const [showAllPending, setShowAllPending] = useState(false);
  const [showAllCompleted, setShowAllCompleted] = useState(false);
  
  // Active/Archive view toggle
  const [taskView, setTaskView] = useState('active'); // 'active' or 'archive'
  const [archivedTasks, setArchivedTasks] = useState([]);
  
  // Filter states for Parent Dashboard
  const [showTaskFilters, setShowTaskFilters] = useState(false);
  const [parentTaskTitleFilter, setParentTaskTitleFilter] = useState('');
  // parentGradeFilter can be controlled by the parent (Dashboard.jsx sidebar) or managed locally.
  const [parentGradeFilterInternal, setParentGradeFilterInternal] = useState('');
  const parentGradeFilter = selectedGradeProp !== undefined ? (selectedGradeProp || '') : parentGradeFilterInternal;
  const setParentGradeFilter = setSelectedGradeProp || setParentGradeFilterInternal;
  const [pendingTaskCounts, setPendingTaskCounts] = useState({}); // Track pending tasks per subject
  const [pendingTasksByOwner, setPendingTasksByOwner] = useState({}); // Track pending tasks per owner (parent/coach)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false); // Feedback modal
  const [isMobile, setIsMobile] = useState(false);
  const [expandedParentCompletedTasks, setExpandedParentCompletedTasks] = useState({});
  const [savedWorksheets, setSavedWorksheets] = useState([]);
  const [showAssignSavedWorksheet, setShowAssignSavedWorksheet] = useState(false);
  const [savedWorksheetToAssign, setSavedWorksheetToAssign] = useState(null);
  const [showEditSavedWorksheet, setShowEditSavedWorksheet] = useState(false);
  const [savedWorksheetToEdit, setSavedWorksheetToEdit] = useState(null);
  const [editingTaskWorksheetId, setEditingTaskWorksheetId] = useState(null);
  const [editingTaskTitleId, setEditingTaskTitleId] = useState(null);
  const [editingTaskTitleValue, setEditingTaskTitleValue] = useState('');
  const [showDeleteSavedConfirm, setShowDeleteSavedConfirm] = useState(false);
  const [pendingDeleteSavedWorksheet, setPendingDeleteSavedWorksheet] = useState(null);
  const shouldHideChatAssistant = Boolean(
    showWorksheetGenerator ||
    showNotesGenerator ||
    showViewSyllabus ||
    showSubmitWork ||
    showChildInsights ||
    showOlympiadModal
  );
  const hasSchoolLink = Boolean(activeChild?.has_school_relationship);


  const getConceptName = (conceptEntry) => (
    typeof conceptEntry === 'string' ? conceptEntry : conceptEntry?.concept
  );

  const buildTargetedNotesConfig = (evaluation) => {
    if (!evaluation) return null;

    const weakConcepts = [
      ...(evaluation?.concept_analysis?.weak || []),
      ...(evaluation?.concept_analysis?.at_risk || [])
    ].map(getConceptName).filter(Boolean);

    const uniqueWeakConcepts = [...new Set(weakConcepts)];
    const learnerName = activeChild?.name || 'the child';

    return {
      type: 'revision',
      subject: evaluation.subject || selectedSubject || '',
      topics: uniqueWeakConcepts,
      reference_text: uniqueWeakConcepts.length > 0
        ? `Generate targeted notes for ${learnerName}. Focus on weak / needs attention concepts from Concept Mastery Analysis: ${uniqueWeakConcepts.join(', ')}. Explain mistakes made, how to avoid them, include quick solving tricks, memory tricks, and extra examples for practice.`
        : `Generate targeted notes for ${learnerName} using Concept Mastery Analysis. Explain mistakes made, how to avoid them, include quick solving tricks, memory tricks, and extra examples for practice.`,
      notes_length: ''
    };
  };


  const openParentTargetedNotes = (evaluation) => {
    const config = buildTargetedNotesConfig(evaluation);
    if (!config) {
      alert('Evaluation data not available for targeted notes.');
      return;
    }

    setNotesGeneratorInitialConfig(config);
    setShowNotesGenerator(true);
  };

  const openDefaultNotesGenerator = () => {
    setNotesGeneratorInitialConfig(null);
    setShowNotesGenerator(true);
  };

  const getWorksheetTopicsLabel = (worksheet) => {
    if (!worksheet) return 'All Topics';
    const topics = worksheet.is_olympiad ? worksheet.olympiad_metadata?.topics : worksheet.topics;
    return formatTopics(topics);
  };

  const getSavedWorksheetTitle = (worksheet) => {
    if (!worksheet) return 'Worksheet';
    return (
      worksheet.saved_title
      || worksheet.title
      || (worksheet.subject ? `${worksheet.subject} Worksheet` : 'Worksheet')
    );
  };

  const isOlympiadWorksheet = (worksheet) => {
    const raw = worksheet?.is_olympiad;
    return raw === true || raw === 'true' || raw === 1;
  };

  const normalizedSelectedSubject = (selectedSubject || '').toLowerCase().trim();
  const shouldFilterBySubject = Boolean(normalizedSelectedSubject && selectedSubject !== 'All Subjects');
  const activeChildGrade = activeChild?.grade ? normalizeGrade(activeChild.grade) : null;
  const filteredSavedWorksheets = savedWorksheets.filter((worksheet) => {
    const worksheetSubject = (worksheet?.subject || '').toLowerCase().trim();
    const isCreatedByUser = worksheet?.creator_id ? worksheet.creator_id === user?.id : true;
    const matchesSubject = !shouldFilterBySubject || worksheetSubject === normalizedSelectedSubject;
    const matchesGrade = !parentGradeFilter || normalizeGrade(worksheet?.grade) === parentGradeFilter;
    return matchesSubject && isCreatedByUser && matchesGrade;
  });
  const filteredArchivedTasks = archivedTasks.filter((taskData) => {
    if (!taskData?.task) return false;
    const taskSubject = (taskData.task.subject || '').toLowerCase().trim();
    const matchesSubject = !shouldFilterBySubject || taskSubject === normalizedSelectedSubject;
    const matchesGrade = !parentGradeFilter || normalizeGrade(taskData.task.grade) === parentGradeFilter;
    return matchesSubject && matchesGrade;
  });

  useEffect(() => {
    const normalizedChildGrade = normalizeGrade(activeChild?.grade);
    setParentGradeFilter(normalizedChildGrade || '');
  }, [activeChild?.id, activeChild?.grade]);

  const requestConceptExplanation = (question) => {
    const message = buildExplainPrompt(question);
    setAiChatRequest({ id: `${Date.now()}-${Math.random()}`, message });
  };


  const loadSavedWorksheets = async () => {
    try {
      const response = await axios.get('/worksheets/saved');
      setSavedWorksheets(response.data.worksheets || []);
    } catch (error) {
      console.error('Error loading saved worksheets:', error);
    }
  };

  useEffect(() => {
    if (activeChild) {
      // Set loading state to prevent rendering incomplete data
      setLoadingChildData(true);
      
      // Clear previous data to prevent stale renders
      setCompletedTasks([]);
      setPendingTasks([]);
      setPendingTasksByOwner({});
      setChildEvaluations({});
      
      // Load evaluations first to ensure they're available when tasks render

      // Fetch tasks, evaluations, and performance concurrently so total
      // latency is max(...) instead of sum(...). Tasks render with the
      // existing "Evaluating…" placeholder if evals arrive a moment later.
      const loadAllData = async () => {
        try {
          console.log('[PARENT LOAD] Starting parallel data load...');
          await Promise.all([
            loadChildEvaluationsForParent(),
            loadTasks(),
            loadChildPerformance(),
          ]);
        } catch (error) {
          console.error('[PARENT LOAD] Error in load sequence:', error);
        } finally {
          setLoadingChildData(false);
        }
      };
      loadAllData();
    }
  }, [activeChild]);

  // Refresh tasks and performance when a task is assigned elsewhere (e.g., worksheet generator)
  useEffect(() => {
    if (!activeChild) return;

    loadTasks();
    loadSchoolTasks();

    if (activeView !== 'parent' && activeView !== 'school') {
      const relationship = coachingRelationships.find(rel => rel.id === activeView);
      if (relationship) {
        loadCoachingTasks(relationship.coach_id);
      }
    }
    loadChildPerformance();
  }, [taskRefreshKey, activeView, activeChild, coachingRelationships]);

  // Reset view to Parent Space if the newly active child has no matching coaching space
  useEffect(() => {
    if (activeView === 'parent' || activeView === 'school' || !activeChild) return;

    const hasActiveRelationship = coachingRelationships.some(
      rel => rel.id === activeView && rel.child_details?.id === activeChild.id
    );

    if (!hasActiveRelationship) {
      setActiveView('parent');
    }
  }, [activeChild, coachingRelationships, activeView]);

  useEffect(() => {
    if (activeView === 'school' && !hasSchoolLink) {
      setActiveView('parent');
    }
  }, [activeView, hasSchoolLink]);

  useEffect(() => {
    if (activeView !== 'parent' && taskView === 'saved') {
      setTaskView('active');
    }
  }, [activeView, taskView]);

  useEffect(() => {
    if (taskView === 'saved') {
      loadSavedWorksheets();
    }
  }, [taskView]);

  useEffect(() => {
    if (childSubjects.length > 0 && !selectedSubject) {
      setSelectedSubject(childSubjects[0]);
    }
  }, [childSubjects, selectedSubject]);

  // Listen for evaluation completion events to refresh tasks and results automatically
  useEffect(() => {
    if (!user?.id) return;

    let socket;
    let reconnectTimeout;
    let pollingInterval;

    const refreshEvaluationData = async () => {
      // Invalidate task cache so loadTasks always fetches fresh data on WS events
      if (activeChild?.id) {
        queryCache.invalidate(`tasks/pending/${activeChild.id}`);
        queryCache.invalidate(`tasks/completed/${activeChild.id}`);
      }
      await loadChildEvaluationsForParent();
      await loadTasks();
    };

    const connectWebSocket = () => {
      try {
        const wsUrl = getUserWebSocketUrl(user.id);
        const ws = new WebSocket(wsUrl);

        ws.onmessage = async (event) => {
          try {
            const { notificationType, childId } = parseWebSocketMessage(event.data);

            if (notificationType === 'evaluation_complete' || notificationType === 'task_submitted') {
              if (!activeChild || !childId || childId === activeChild.id) {
                await refreshEvaluationData();
              }
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };

        ws.onclose = (event) => {
          if (event.code !== 1000) {
            console.warn('WebSocket disconnected, reconnecting...');
          }
          reconnectTimeout = setTimeout(connectWebSocket, 2000);
        };

        ws.onerror = () => {
          console.warn('WebSocket connection error detected. Falling back to polling and reconnect.');
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            ws.close();
          }
        };

        socket = ws;
      } catch (error) {
        console.error('Error connecting WebSocket:', error);
      }
    };

    const startPolling = () => {
      if (pollingInterval) clearInterval(pollingInterval);
      // Fallback polling to keep tasks and evaluations fresh even if WebSocket drops silently
      pollingInterval = setInterval(() => {
        if (!activeChild) return;
        refreshEvaluationData();
      }, 10000);
    };

    connectWebSocket();
    startPolling();

    // When the Capacitor app returns to the foreground the OS may have killed
    // the WebSocket connection silently.  Listen for the custom event dispatched
    // by capacitor.js setupAppListeners and force an immediate reconnect.
    const handleAppResume = ({ detail }) => {
      if (!detail?.isActive) return;
      // Close the stale socket — onclose will schedule reconnectTimeout
      if (socket && socket.readyState !== WebSocket.CLOSED) {
        socket.close();
      }
      // Also flush any offline submissions queued while the app was backgrounded
      import('./utils/offlineSubmissionQueue').then(({ flushOfflineSubmissions }) => {
        flushOfflineSubmissions().catch(() => {});
      });
      // Refresh data immediately so the UI isn't stale after resume
      refreshEvaluationData().catch(() => {});
    };

    window.addEventListener('appStateChange', handleAppResume);

    return () => {
      if (socket) {
        socket.close();
      }
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      window.removeEventListener('appStateChange', handleAppResume);
    };
  }, [user?.id, activeChild?.id]);

  // Reload performance when selectedSubject changes
  useEffect(() => {
    if (activeChild && selectedSubject) {
      loadChildPerformance();
    }
  }, [selectedSubject]);

  useEffect(() => {
    if (activeChild) {
      loadChildPerformance();
    }
  }, [activeView, activeChild]);

  // Load coaching tasks when activeView changes
  useEffect(() => {
    if (activeView !== 'parent' && activeView !== 'school' && activeChild) {
      // Find the coaching relationship for the selected view
      const relationship = coachingRelationships.find(rel => rel.id === activeView);
      if (relationship) {
        loadCoachingTasks(relationship.coach_id);
      }
    }
  }, [activeView, activeChild, coachingRelationships]);

  useEffect(() => {
    if (activeView === 'school' && activeChild) {
      loadSchoolTasks();
    }
  }, [activeView, activeChild]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const handleResize = () => setIsMobile(mediaQuery.matches);

    handleResize();
    mediaQuery.addEventListener('change', handleResize);

    return () => mediaQuery.removeEventListener('change', handleResize);
  }, []);

  useEffect(() => {
    const currentCompleted = activeView === 'parent'
      ? completedTasks
      : activeView === 'school'
      ? schoolTasks.completed
      : coachingTasks.completed;

    setExpandedParentCompletedTasks((prev) => {
      const next = {};
      currentCompleted.forEach((task) => {
        const defaultOpen = isMobile ? false : true;
        next[task.id] = prev[task.id] ?? defaultOpen;
      });
      return next;
    });
  }, [completedTasks, coachingTasks.completed, isMobile, activeView]);

  const getSubjectsForActiveView = () => {
    if (!activeChild) return [];

    if (activeView === 'parent') {
      return childSubjects;
    }

    if (activeView === 'school') {
      return childSubjects;
    }

    const activeRelationship = coachingRelationships.find(r => r.id === activeView);
    if (!activeRelationship) return [];

    const coachSubjects = coachingRelationships
      .filter(rel => rel.child_details?.id === activeChild.id && rel.coach_id === activeRelationship.coach_id)
      .map(rel => rel.subject)
      .filter(Boolean);

    const uniqueCoachSubjects = [...new Set(coachSubjects)];
    return childSubjects.filter(subject => uniqueCoachSubjects.includes(subject));
  };

  // Ensure a subject is selected when switching spaces or when subjects change
  useEffect(() => {
    const availableSubjects = getSubjectsForActiveView();
    if (availableSubjects.length === 0) return;

    if (!selectedSubject || !availableSubjects.includes(selectedSubject)) {
      setSelectedSubject(availableSubjects[0]);
    }
  }, [activeView, childSubjects, activeChild, coachingRelationships, selectedSubject]); 

  // Helper function to get current tasks based on active view and selected subject
  const getCurrentTasks = () => {
    let tasks;
    if (activeView === 'parent') {
      tasks = { 
        pending: pendingTasks, 
        completed: completedTasks, 
        evaluations: childEvaluations 
      };
    } else if (activeView === 'school') {
      tasks = {
        pending: schoolTasks.pending,
        completed: schoolTasks.completed,
        evaluations: schoolEvaluations
      };
    } else {
      tasks = { 
        pending: coachingTasks.pending, 
        completed: coachingTasks.completed, 
        evaluations: coachingEvaluations 
      };
    }
    
    // Apply subject filtering if a specific subject is selected
    if (selectedSubject && selectedSubject !== 'All Subjects') {
      // Check if active child is in grades 1-3 for subject mapping
      const isGrade1to3 = activeChild && activeChild.grade >= 1 && activeChild.grade <= 3;
      
      tasks.pending = tasks.pending.filter(task => {
        let taskSubject = task.subject;
        
        // Map Science and Social Studies Olympiad to Environmental Studies for Grades 1-3
        if (isGrade1to3 && task.is_olympiad) {
          if (taskSubject === 'Science' || taskSubject === 'Social Studies') {
            taskSubject = 'Environmental Studies';
          }
        }
        
        return taskSubject === selectedSubject;
      });
      
      tasks.completed = tasks.completed.filter(task => {
        let taskSubject = task.subject;
        
        // Map Science and Social Studies Olympiad to Environmental Studies for Grades 1-3
        if (isGrade1to3 && task.is_olympiad) {
          if (taskSubject === 'Science' || taskSubject === 'Social Studies') {
            taskSubject = 'Environmental Studies';
          }
        }
        
        return taskSubject === selectedSubject;
      });
    }
    
    return tasks;
  };


  // Render KaTeX in practice sheet modal when it opens
  useEffect(() => {
    if (showPracticeSheetModal && currentPracticeSheet && window.renderMathInElement) {
      setTimeout(() => {
        const modalContent = document.querySelector('.fixed.inset-0');
        if (modalContent) {
          try {
            window.renderMathInElement(modalContent, {
              delimiters: [
                { left: '$$', right: '$$', display: true },
                { left: '$', right: '$', display: false },
                { left: '\\[', right: '\\]', display: true },
                { left: '\\(', right: '\\)', display: false },
              ],
              throwOnError: false,
            });
          } catch (error) {
            console.error('Error rendering KaTeX in practice sheet modal:', error);
          }
        }
      }, 100);
    }
  }, [showPracticeSheetModal, currentPracticeSheet]);

  // Load and process TikZJax for practice sheet modal
  useEffect(() => {
    if (showPracticeSheetModal && currentPracticeSheet) {
      // Load TikZJax if not already loaded
      const loadTikZJax = () => {
        if (document.querySelector('script[src*="tikzjax"]')) {
          // Already loaded, just process diagrams
          processTikZDiagrams();
          return;
        }
        
        // Load TikZJax CSS
        if (!document.querySelector('link[href*="tikzjax"]')) {
          const cssLink = document.createElement('link');
          cssLink.rel = 'stylesheet';
          cssLink.type = 'text/css';
          cssLink.href = 'https://tikzjax.com/v1/fonts.css';
          document.head.appendChild(cssLink);
        }
        
        // Load TikZJax JS
        const script = document.createElement('script');
        script.src = 'https://tikzjax.com/v1/tikzjax.js';
        script.onload = () => {
          console.log('TikZJax loaded in practice sheet modal');
          setTimeout(processTikZDiagrams, 500);
        };
        script.onerror = () => {
          console.error('Failed to load TikZJax in practice sheet modal');
        };
        document.head.appendChild(script);
      };
      
      const processTikZDiagrams = () => {
        try {
          const tikzScripts = document.querySelectorAll('.fixed.inset-0 script[type="text/tikz"]');
          console.log(`Found ${tikzScripts.length} TikZ diagrams in practice sheet`);
          
          if (tikzScripts.length > 0 && window.tikzjax) {
            tikzScripts.forEach((script, index) => {
              try {
                console.log(`Processing TikZ diagram ${index + 1} in practice sheet`);
                window.tikzjax.process(script);
              } catch (error) {
                console.error(`Error processing TikZ diagram ${index + 1}:`, error);
              }
            });
          } else if (tikzScripts.length > 0) {
            console.log('Triggering TikZJax auto-processing in practice sheet');
            document.dispatchEvent(new Event('DOMContentLoaded'));
          }
        } catch (error) {
          console.error('Error processing TikZ diagrams in practice sheet:', error);
        }
      };
      
      loadTikZJax();
    }
  }, [showPracticeSheetModal, currentPracticeSheet]);


  // Helper function to get current view title
  const getCurrentViewTitle = () => {
    if (activeView === 'parent') {
      return 'Parent Space';
    }
    if (activeView === 'school') {
      return 'School Space';
    }
    const relationship = coachingRelationships.find(rel => rel.id === activeView);
    return relationship ? `${relationship.coach_details?.name}'s Tasks` : 'Coaching Space';
  };

  const getTaskSubmissionTimestamp = (task) => {
    const submittedAt = task.submitted_at || task.evaluated_at || task.completed_at || task.updated_at || task.created_at;
    if (!submittedAt) return 0;
    const timestamp = new Date(submittedAt).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  };

  const sortTasksBySubmissionDate = (tasks) => (
    [...tasks].sort((a, b) => getTaskSubmissionTimestamp(b) - getTaskSubmissionTimestamp(a))
  );

  const isChallengeTask = (task) => task?.is_challenge_task || task?.assigned_by_role === 'qb_admin';

  async function loadTasks() {
    if (!activeChild) return;
    
    try {
      const TASK_TTL = 30 * 1000; // 30 s — tasks update when evaluations complete
      const [pendingResponse, completedResponse] = await Promise.all([
        cachedFetch(
          `tasks/pending/${activeChild.id}`,
          () => axios.get(`/children/${activeChild.id}/tasks/pending`),
          TASK_TTL
        ),
        cachedFetch(
          `tasks/completed/${activeChild.id}`,
          () => axios.get(`/children/${activeChild.id}/tasks/completed`),
          TASK_TTL
        ),
      ]);
      
      // Filter tasks by assignor - only show parent-assigned tasks in Parent Space
      const allPendingTasks = pendingResponse.data.tasks;
      const allCompletedTasks = completedResponse.data.tasks;
      
      // Filter for parent-assigned tasks (owner_id === user.id) + challenge tasks
      const parentPendingTasks = allPendingTasks.filter(
        task => task.owner_id === user.id || isChallengeTask(task)
      );
      const parentCompletedTasks = sortTasksBySubmissionDate(
        allCompletedTasks.filter(task => task.owner_id === user.id || isChallengeTask(task))
      );
      
      setPendingTasks(parentPendingTasks);
      setCompletedTasks(parentCompletedTasks);
      
      // Calculate pending task counts per subject (for all pending tasks, not just parent's)
      const counts = {};
      const ownerCounts = {};
      allPendingTasks.forEach(task => {
        const subject = task.subject || 'Unknown';
        counts[subject] = (counts[subject] || 0) + 1;

        const ownerId = task.owner_id || 'unknown';
        ownerCounts[ownerId] = (ownerCounts[ownerId] || 0) + 1;
      });
      setPendingTaskCounts(counts);
      setPendingTasksByOwner(ownerCounts);

      const schoolPendingTasks = allPendingTasks.filter(task => task.assigned_by_role === 'school_teacher');
      const schoolCompletedTasks = sortTasksBySubmissionDate(
        allCompletedTasks.filter(task => task.assigned_by_role === 'school_teacher')
      );
      setSchoolTasks({
        pending: schoolPendingTasks,
        completed: schoolCompletedTasks
      });

    } catch (error) {
      console.error('Error loading tasks:', error);
    }
  };

  useEffect(() => {
    const schoolEvaluationsMap = {};
    for (const task of [...schoolTasks.pending, ...schoolTasks.completed]) {
      if (childEvaluations[task.id]) {
        schoolEvaluationsMap[task.id] = childEvaluations[task.id];
      }
    }
    setSchoolEvaluations(schoolEvaluationsMap);
  }, [childEvaluations, schoolTasks.pending, schoolTasks.completed]);

  const loadChildPerformance = async () => {
    if (activeChild) {
      try {
        // Add subject filter if a specific subject is selected
        const params = selectedSubject && selectedSubject !== 'All Subjects' ? { subject: selectedSubject } : {};
        if (activeView === 'parent') {
          params.owner_id = user?.id;
        } else if (activeView === 'school') {
          params.assigned_by_role = 'school_teacher';
        } else {
          const relationship = coachingRelationships.find(rel => rel.id === activeView);
          if (relationship) {
            params.owner_id = relationship.coach_id;
          }
        }
        const response = await axios.get(`/children/${activeChild.id}/performance`, { params });
        setChildPerformance(response.data);
      } catch (error) {
        console.error('Error loading child performance:', error);
      }
    }
  };

  async function loadChildEvaluationsForParent() {
    if (activeChild) {
      try {
        console.log(`[PARENT DEBUG] Loading evaluations for child: ${activeChild.id}`);
        const response = await axios.get(`/children/${activeChild.id}/evaluations`);
        console.log(`[PARENT DEBUG] Evaluation response:`, response.data);
        
        // Create a map of task_id -> evaluation for easy lookup
        const evaluationsMap = {};
        if (response.data.evaluations && response.data.evaluations.length > 0) {
          response.data.evaluations.forEach(item => {
            const evaluationWithTask = {
              ...item.evaluation,
              _taskInfo: item.task || null
            };
            evaluationsMap[item.evaluation.task_id] = evaluationWithTask;
            console.log(`[PARENT DEBUG] Added evaluation for task ${item.evaluation.task_id}`);
            if (item.task?.id && item.task.id !== item.evaluation.task_id) {
              evaluationsMap[item.task.id] = evaluationWithTask;
            }
          });
          console.log(`[PARENT DEBUG] Total evaluations loaded: ${Object.keys(evaluationsMap).length}`);
        } else {
          console.log(`[PARENT DEBUG] No evaluations found for child ${activeChild.id}`);
        }
        
        setChildEvaluations(evaluationsMap);
      } catch (error) {
        console.error('Error loading child evaluations:', error);
      }
    }
  };

  // Export evaluation report as PDF
  const handleExportReport = (evaluation) => {
    console.log('Exporting report for evaluation:', evaluation);
    
    // Safe date formatting function
    const formatDate = (dateValue) => {
      try {
        if (!dateValue) return 'N/A';
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString();
      } catch (e) {
        return 'N/A';
      }
    };

    const formatDateForFilename = (dateValue) => {
      try {
        if (!dateValue) return 'report';
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return 'report';
        return date.toISOString().split('T')[0];
      } catch (e) {
        return 'report';
      }
    };
    
    // Create HTML content for PDF
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Evaluation Report</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #3B82F6; padding-bottom: 15px; }
          .header h1 { color: #1E40AF; margin: 0; font-size: 24px; }
          .header p { color: #6B7280; margin: 5px 0; }
          .summary { background: #EFF6FF; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .summary-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
          .summary-item { padding: 10px; }
          .summary-item label { font-weight: bold; color: #1E40AF; display: block; margin-bottom: 5px; }
          .summary-item value { color: #374151; font-size: 18px; }
          .section { margin-bottom: 25px; }
          .section-title { color: #1E40AF; font-size: 18px; font-weight: bold; margin-bottom: 15px; border-bottom: 1px solid #E5E7EB; padding-bottom: 5px; }
          .question { background: #F9FAFB; padding: 15px; margin-bottom: 15px; border-left: 4px solid #3B82F6; border-radius: 4px; }
          .question-header { font-weight: bold; color: #1F2937; margin-bottom: 10px; }
          .question-text { color: #374151; margin-bottom: 10px; line-height: 1.6; }
          .answer-row { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 8px; }
          .answer-label { font-weight: 600; color: #6B7280; }
          .answer-value { color: #1F2937; }
          .marks { color: #059669; font-weight: bold; }
          .marks.partial { color: #F59E0B; }
          .marks.incorrect { color: #DC2626; }
          .feedback { background: #FEF3C7; padding: 10px; border-radius: 4px; margin-top: 8px; font-size: 14px; color: #92400E; }
          .improvement { background: #DBEAFE; padding: 10px; border-radius: 4px; margin-top: 8px; font-size: 14px; color: #1E40AF; }
          .strengths-weaknesses { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 20px; }
          .sw-box { background: #F9FAFB; padding: 15px; border-radius: 8px; }
          .sw-box.strengths { border-left: 4px solid #10B981; }
          .sw-box.weaknesses { border-left: 4px solid #F59E0B; }
          .sw-title { font-weight: bold; margin-bottom: 10px; }
          .sw-box.strengths .sw-title { color: #065F46; }
          .sw-box.weaknesses .sw-title { color: #92400E; }
          .sw-list { list-style: none; padding: 0; }
          .sw-list li { padding: 5px 0; color: #374151; }
          .footer { text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #E5E7EB; color: #6B7280; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Edsupp - Evaluation Report</h1>
          <p>${evaluation._taskInfo?.name || 'General Evaluation'}</p>
          ${activeChild ? `<p>Student: ${getFirstName(activeChild.name) || activeChild.name}</p>` : ''}
          <p>Date: ${formatDate(evaluation._taskInfo?.submitted_at || evaluation._taskInfo?.created_at ||evaluation.timestamp)}</p>
        </div>

        <div class="summary">
          <div class="summary-grid">
            <div class="summary-item">
              <label>Subject:</label>
              <value>${selectedSubject || 'N/A'}</value>
            </div>
            <div class="summary-item">
              <label>Grade:</label>
              <value>${activeChild?.grade || 'N/A'}</value>
            </div>
            <div class="summary-item">
              <label>Total Questions:</label>
              <value>${evaluation.question_analysis?.length || 0}</value>
            </div>
            <div class="summary-item">
              <label>Score:</label>
              <value>${evaluation.overall_score || 0} %</value>
            </div>
          </div>
        </div>

        ${evaluation.overall_strengths && evaluation.overall_weaknesses ? `
        <div class="strengths-weaknesses">
          <div class="sw-box strengths">
            <div class="sw-title">✅ Strengths</div>
            <ul class="sw-list">
              ${evaluation.overall_strengths.map(s => `<li>• ${s}</li>`).join('')}
            </ul>
          </div>
          <div class="sw-box weaknesses">
            <div class="sw-title">⚠️ Areas for Improvement</div>
            <ul class="sw-list">
              ${evaluation.overall_weaknesses.map(w => `<li>• ${w}</li>`).join('')}
            </ul>
          </div>
        </div>
        ` : ''}

        <div class="section">
          <div class="section-title">📝 Question-wise Analysis</div>
          ${(evaluation.question_analysis || []).map((qa, index) => {
            const marksClass = qa.is_correct === 'correct' ? 'marks' : 
                              qa.is_correct === 'partial' ? 'marks partial' : 'marks incorrect';
            const percentage = qa.max_marks > 0 ? Math.round(((qa.partial_credit || 0) * 100)) : 0;
            
            return `
              <div class="question">
                <div class="question-header">Question ${qa.q_number || index + 1} (${qa.marks_awarded || 0}/${qa.max_marks || 0} marks - ${percentage}%)</div>
                <div class="question-text"><strong>Q:</strong> ${(qa.question_text || '').replace(/<[^>]*>/g, '')}</div>
                <div class="answer-row">
                  <div><span class="answer-label">Student Answer:</span> <span class="answer-value">${(qa.student_answer || 'Not Answered').replace(/<[^>]*>/g, '')}</span></div>
                  <div><span class="answer-label">Correct Answer:</span> <span class="answer-value">${(qa.correct_answer || 'N/A').replace(/<[^>]*>/g, '')}</span></div>
                </div>
                ${qa.feedback ? `<div class="feedback"><strong>💡 Feedback:</strong> ${qa.feedback}</div>` : ''}
                ${qa.improvement_tip ? `<div class="improvement"><strong>📚 Improvement Tip:</strong> ${qa.improvement_tip}</div>` : ''}
              </div>
            `;
          }).join('')}
        </div>

        ${evaluation.learning_analytics?.recommended_activities ? `
        <div class="section">
          <div class="section-title">🎯 Recommended Activities</div>
          <ul class="sw-list">
            ${evaluation.learning_analytics.recommended_activities.map(a => `<li>• ${a}</li>`).join('')}
          </ul>
        </div>
        ` : ''}

        <div class="footer">
          <p>Generated by Edsupp - Your Learning Companion</p>
          <p>This report is for educational purposes only.</p>
        </div>
      </body>
      </html>
    `;

    console.log('Starting PDF generation...');
    console.log('Evaluation data:', evaluation);

    // Create a wrapper for the PDF content
    const wrapper = document.createElement('div');
    wrapper.id = 'pdf-wrapper';
    wrapper.style.position = 'fixed';
    wrapper.style.top = '0';
    wrapper.style.left = '0';
    wrapper.style.width = '100vw';
    wrapper.style.height = '100vh';
    wrapper.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    wrapper.style.zIndex = '99999';
    wrapper.style.display = 'flex';
    wrapper.style.alignItems = 'center';
    wrapper.style.justifyContent = 'center';
    wrapper.style.overflow = 'auto';

    // Create the content container
    const container = document.createElement('div');
    container.id = 'pdf-export-container';
    container.innerHTML = htmlContent;
    container.style.width = '210mm';
    container.style.minHeight = '297mm';
    container.style.backgroundColor = '#ffffff';
    container.style.boxShadow = '0 0 20px rgba(0,0,0,0.3)';
    
    wrapper.appendChild(container);
    document.body.appendChild(wrapper);
    
    console.log('Container created and visible on screen');
    console.log('Container HTML length:', container.innerHTML.length);

    // Wait for user to see it briefly, then generate PDF
    setTimeout(() => {
      console.log('Starting html2pdf conversion...');
      
      const opt = {
        margin: [10, 10, 10, 10],
        filename: `evaluation-report-${formatDateForFilename(evaluation.timestamp)}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: true,
          backgroundColor: '#ffffff'
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'portrait'
        }
      };

      html2pdf()
        .set(opt)
        .from(container)
        .save()
        .then(() => {
          console.log('✅ PDF saved successfully');
          document.body.removeChild(wrapper);
        })
        .catch(err => {
          console.error('❌ PDF generation error:', err);
          if (document.body.contains(wrapper)) {
            document.body.removeChild(wrapper);
          }
          alert('Failed to generate PDF. Error: ' + err.message);
        });
    }, 500);
  };

  const loadArchivedTasksForParent = async () => {
    if (activeChild) {
      try {
        const response = await axios.get('/tasks/archived', {
          params: { child_id: activeChild.id }
        });
        console.log('Archived tasks loaded:', response.data);
        setArchivedTasks(response.data.archived_tasks || []);
      } catch (error) {
        console.error('Error loading archived tasks:', error);
        setArchivedTasks([]);
      }
    }
  };

  // Filter parent tasks by title and grade (subject + EVS mapping for grades 1-3 happens in getCurrentTasks)
  const filterParentTasks = (tasks) => {
    // Skip grade filtering in Coaching Teacher space: a batch's grade can
    // legitimately differ from a student's own profile grade, so matching
    // on it hid every task whenever the two didn't line up.
    const isCoachingTeacherSpace = activeView !== 'parent' && activeView !== 'school';
    const effectiveGradeFilter = isCoachingTeacherSpace ? '' : parentGradeFilter;

    if (!parentTaskTitleFilter && !effectiveGradeFilter) {
      return tasks;
    }
    
    return tasks.filter(task => {
      const titleMatch = !parentTaskTitleFilter || 
        task.title?.toLowerCase().includes(parentTaskTitleFilter.toLowerCase());
      
      // Handle subject matching with Grade 1-3 mapping for olympiad tasks
      const taskGrade = task.grade ?? task.worksheet?.grade;
      const gradeMatch = !effectiveGradeFilter || normalizeGrade(taskGrade) === effectiveGradeFilter;
      
      return titleMatch && gradeMatch;
    });
  };

  const getAvailableGradeFilters = () => {
    const { pending, completed } = getCurrentTasks();
    const childGrade = normalizeGrade(activeChild?.grade);
    const grades = [...pending, ...completed]
      .map(task => task.grade ?? task.worksheet?.grade)
      .map(grade => normalizeGrade(grade))
      .filter(Boolean);
    if (childGrade) {
      grades.push(childGrade);
    }
    return [...new Set(grades)].sort((a, b) => {
      const numericA = Number(a);
      const numericB = Number(b);
      const areNumbers = !Number.isNaN(numericA) && !Number.isNaN(numericB);
      return areNumbers
        ? numericA - numericB
        : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    });
  };
  
  const parentAvailableGrades = getAvailableGradeFilters();

  // Get pending task counts filtered by active view
  const getFilteredTaskCounts = () => {
    // Get all pending tasks
    const allTasks = [...pendingTasks, ...coachingTasks.pending, ...schoolTasks.pending];
    
    // Filter by active view
    let filteredTasks;
    if (activeView === 'parent') {
      // Show only parent-assigned tasks + challenge tasks
      filteredTasks = allTasks.filter(task => task.owner_id === user.id || isChallengeTask(task));
    } else if (activeView === 'school') {
      filteredTasks = allTasks.filter(task => task.assigned_by_role === 'school_teacher');
    } else {
      // Show only specific coach's tasks
      const activeRelationship = coachingRelationships.find(r => r.id === activeView);
      if (activeRelationship) {
        filteredTasks = allTasks.filter(task => task.owner_id === activeRelationship.coach_id);
      } else {
        filteredTasks = [];
      }
    }
    
    // Calculate counts per subject with Grade 1-3 mapping
    const isGrade1to3 = activeChild && activeChild.grade >= 1 && activeChild.grade <= 3;
    const counts = {};
    filteredTasks.forEach(task => {
      let subject = task.subject || 'Unknown';
      
      // Map Science and Social Studies Olympiad to Environmental Studies for Grades 1-3
      if (isGrade1to3 && task.is_olympiad) {
        if (subject === 'Science' || subject === 'Social Studies') {
          subject = 'Environmental Studies';
        }
      }
      
      counts[subject] = (counts[subject] || 0) + 1;
    });
    
    return counts;
  };

  const getGradeTaskCounts = () => {
    const allTasksCombined = [...pendingTasks, ...coachingTasks.pending, ...schoolTasks.pending];

    let filteredTasks;
    if (activeView === 'parent') {
      filteredTasks = allTasksCombined.filter(task => task.owner_id === user.id || isChallengeTask(task));
    } else if (activeView === 'school') {
      filteredTasks = allTasksCombined.filter(task => task.assigned_by_role === 'school_teacher');
    } else {
      const activeRelationship = coachingRelationships.find(r => r.id === activeView);
      filteredTasks = activeRelationship
        ? allTasksCombined.filter(task => task.owner_id === activeRelationship.coach_id)
        : [];
    }

    if (selectedSubject && selectedSubject !== 'All Subjects') {
      const isGrade1to3 = activeChild && activeChild.grade >= 1 && activeChild.grade <= 3;
      filteredTasks = filteredTasks.filter(task => {
        let subject = task.subject;
        if (isGrade1to3 && task.is_olympiad) {
          if (subject === 'Science' || subject === 'Social Studies') subject = 'Environmental Studies';
        }
        return subject === selectedSubject;
      });
    }

    const counts = {};
    filteredTasks.forEach(task => {
      const grade = normalizeGrade(task.grade ?? task.worksheet?.grade);
      if (grade) counts[grade] = (counts[grade] || 0) + 1;
    });
    return counts;
  };

  // Load coaching-specific tasks for the current active view
  const loadCoachingTasks = async (coachId) => {
    if (!activeChild) return;
    
    try {
      const [pendingResponse, completedResponse] = await Promise.all([
        axios.get(`/children/${activeChild.id}/tasks/pending`),
        axios.get(`/children/${activeChild.id}/tasks/completed`)
      ]);
      
      // Filter tasks to show only coach-assigned tasks
      const allPendingTasks = pendingResponse.data.tasks || [];
      const allCompletedTasks = completedResponse.data.tasks || [];
      
      const coachPendingTasks = allPendingTasks.filter(task => task.owner_id === coachId);
      const coachCompletedTasks = allCompletedTasks.filter(task => task.owner_id === coachId);
      

      // Update pending counts by owner to keep badges fresh when switching views
      const ownerCounts = {};
      allPendingTasks.forEach(task => {
        const ownerId = task.owner_id || 'unknown';
        ownerCounts[ownerId] = (ownerCounts[ownerId] || 0) + 1;
      });
      setPendingTasksByOwner(ownerCounts);

      setCoachingTasks({
        pending: coachPendingTasks,
        completed: coachCompletedTasks
      });
      
      // Load evaluations for coaching tasks
      const coachingEvaluationsMap = {};
      for (const task of [...coachPendingTasks, ...coachCompletedTasks]) {
        if (childEvaluations[task.id]) {
          coachingEvaluationsMap[task.id] = childEvaluations[task.id];
        }
      }
      setCoachingEvaluations(coachingEvaluationsMap);
      
    } catch (error) {
      console.error('Error loading coaching tasks:', error);
    }
  };

  const loadSchoolTasks = async () => {
    if (!activeChild) return;

    try {
      const [pendingResponse, completedResponse] = await Promise.all([
        axios.get(`/children/${activeChild.id}/tasks/pending`),
        axios.get(`/children/${activeChild.id}/tasks/completed`)
      ]);

      const allPendingTasks = pendingResponse.data.tasks || [];
      const allCompletedTasks = completedResponse.data.tasks || [];

      const schoolPendingTasks = allPendingTasks.filter(task => task.assigned_by_role === 'school_teacher');
      const schoolCompletedTasks = allCompletedTasks.filter(task => task.assigned_by_role === 'school_teacher');

      setSchoolTasks({
        pending: schoolPendingTasks,
        completed: sortTasksBySubmissionDate(schoolCompletedTasks)
      });

      const schoolEvaluationsMap = {};
      for (const task of [...schoolPendingTasks, ...schoolCompletedTasks]) {
        if (childEvaluations[task.id]) {
          schoolEvaluationsMap[task.id] = childEvaluations[task.id];
        }
      }
      setSchoolEvaluations(schoolEvaluationsMap);
    } catch (error) {
      console.error('Error loading school tasks:', error);
    }
  };

  const viewTaskEvaluationAsParent = async (taskId) => {
    try {
      const response = await axios.get(`/tasks/${taskId}/evaluation`);
      if (response.data.evaluation) {
        // Add task info from the evaluation response
        const evaluationWithTask = {
          ...response.data.evaluation,
          _taskInfo: response.data.task  // Use task data from evaluation response
        };
        
        setCurrentParentEvaluation(evaluationWithTask);
        setShowParentEvaluationModal(true);
      } else {
        alert('No evaluation available for this task yet.');
      }
    } catch (error) {
      console.error('Error loading task evaluation:', error);
      alert('Error loading evaluation details.');
    }
  };

  const toggleParentCompletedTask = (taskId) => {
    setExpandedParentCompletedTasks((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  const pollForEvaluationsAsParent = (childId, retries = 8) => {
    const pollInterval = 3000; // 3 seconds
    
    const poll = async (remainingRetries) => {
      if (remainingRetries <= 0) {
        console.log('Parent evaluation polling timeout - Evaluation may still be in progress');
        return;
      }
      
      try {
        await loadChildEvaluationsForParent();
        await loadTasks(); // Also reload tasks to update task states
        
        // Check if we have new evaluations
        const currentEvaluationCount = Object.keys(childEvaluations).length;
        console.log(`Parent poll attempt ${9 - remainingRetries}: Found ${currentEvaluationCount} evaluations`);
        
        // Continue polling
        setTimeout(() => poll(remainingRetries - 1), pollInterval);
      } catch (error) {
        console.error('Error during parent evaluation polling:', error);
        // Continue polling even on error
        setTimeout(() => poll(remainingRetries - 1), pollInterval);
      }
    };
    
    // Start polling after a short delay
    setTimeout(() => poll(retries), 2000);
  };

  const viewAnswerKey = async (taskId) => {
    try {
      const response = await axios.get(`/tasks/${taskId}/answer-key`);
      setCurrentAnswerKey(response.data);
      setShowAnswerKeyModal(true);
    } catch (error) {
      console.error('Error loading answer key:', error);
      alert('Error loading answer key. Please try again.');
    }
  };

  const viewComparison = async (taskId) => {
    try {
      console.log(`[COMPARISON] Attempting to load comparison for task: ${taskId}`);
      const response = await axios.get(`/tasks/${taskId}/comparison`);
      console.log(`[COMPARISON] Successfully loaded comparison for task: ${taskId}`);
      
      // Also fetch evaluation to get task info
      const evaluationResponse = await axios.get(`/tasks/${taskId}/evaluation`).catch(() => ({ data: { evaluation: null, task: null } }));
      const resolvedEvaluation = evaluationResponse.data.evaluation || response.data.evaluation || null;
      const resolvedEvaluationWithTask = resolvedEvaluation
        ? {
          ...resolvedEvaluation,
          _taskInfo: evaluationResponse.data.task || response.data.task || null
        }
        : null;
      
      // Add task info and evaluation to comparison data
      const comparisonWithExtras = {
        ...response.data,
        _taskInfo: evaluationResponse.data.task,  // Use task data from evaluation response
        _evaluation: resolvedEvaluationWithTask
      };
      
      setCurrentParentEvaluation(resolvedEvaluationWithTask);
      setCurrentComparison(comparisonWithExtras);
      setShowComparisonModal(true);
    } catch (error) {
      console.error(`[COMPARISON] Error loading comparison for task ${taskId}:`, error);
      
      // Provide more specific error messages
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.detail || 'Unknown error';
        
        if (status === 400) {
          alert('This task must be completed before you can view the answer comparison.');
        } else if (status === 404) {
          if (message.includes('Detailed evaluation not available')) {
            alert('Evaluation is still in progress. Please wait a few moments and try again.');
          } else if (message.includes('No evaluation found')) {
            alert('No evaluation available for this task. The task may not have been submitted yet.');
          } else {
            alert(`Comparison data not found: ${message}`);
          }
        } else if (status === 403) {
          alert('You do not have permission to view this comparison.');
        } else {
          alert(`Error loading comparison: ${message}`);
        }
      } else {
        alert('Network error. Please check your connection and try again.');
      }
    }
  };

  const archiveTask = async (taskId) => {
    try {
      await axios.patch(`/tasks/${taskId}/archive`);
      alert('Task archived successfully!');
      loadTasks(); // Reload tasks to reflect the change
      loadChildEvaluationsForParent(); // Reload evaluations
    } catch (error) {
      console.error('Error archiving task:', error);
      alert('Error archiving task. Please try again.');
    }
  };

  const unarchiveTask = async (taskId) => {
    try {
      await axios.patch(`/tasks/${taskId}/unarchive`);
      alert('Task restored successfully!');
      loadTasks();
      loadChildEvaluationsForParent();
      loadArchivedTasksForParent();
    } catch (error) {
      console.error('Error restoring task:', error);
      alert(error.response?.data?.detail || 'Error restoring task. Please try again.');
    }
  };

  const reassignTask = async (taskId) => {
    try {
      await axios.patch(`/tasks/${taskId}/reassign`);
      alert('Task reassigned successfully! It has been moved back to pending tasks.');
      loadTasks(); // Reload tasks to reflect the change
    } catch (error) {
      console.error('Error reassigning task:', error);
      alert('Error reassigning task. Please try again.');
    }
  };

  const reEvaluateTask = async (taskId) => {
    try {
      await axios.patch(`/tasks/${taskId}/re-evaluate`);
      alert('Re-evaluation queued! The results will refresh when ready.');
      await loadTasks();
      await loadChildEvaluationsForParent();
      if (activeChild) {
        pollForEvaluationsAsParent(activeChild.id);
      }
    } catch (error) {
      console.error('Error re-evaluating task:', error);
      alert(error.response?.data?.detail || 'Error re-evaluating task. Please try again.');
    }
  };

  const viewPracticeSheet = (taskId) => {
    openPracticeSheet(taskId);
  };

  const handleAssignSavedWorksheet = (worksheet) => {
    if (!worksheet) return;
    if (!activeChild) {
      alert('Please select a child before assigning a saved worksheet.');
      return;
    }
    setSavedWorksheetToAssign(worksheet);
    setShowAssignSavedWorksheet(true);
  };

  const handleEditSavedWorksheet = (worksheet) => {
    if (!worksheet) return;
    setSavedWorksheetToEdit(worksheet);
    setShowEditSavedWorksheet(true);
  };

  const handleEditAssignedWorksheet = async (task) => {
    if (!task?.id) return;

    setEditingTaskWorksheetId(task.id);
    try {
      const response = await axios.get(`/tasks/${task.id}/worksheet`);
      const worksheet = response?.data?.worksheet || response?.data;

      if (!worksheet) {
        throw new Error('Worksheet data not found for this task');
      }

      setSavedWorksheetToEdit(worksheet);
      setShowEditSavedWorksheet(true);
    } catch (error) {
      console.error('Error loading assigned worksheet for editing:', error);
      alert(error.response?.data?.detail || 'Unable to open this worksheet for editing. Please try again.');
    } finally {
      setEditingTaskWorksheetId(null);
    }
  };

  const handleDeleteSavedWorksheet = async (worksheet) => {
    try {
      if (!worksheet?.id) {
        alert('Unable to delete worksheet: Worksheet ID not found');
        return;
      }

      await axios.delete(`/worksheets/${worksheet.id}/saved`);
      alert('Saved worksheet deleted successfully!');
      await loadSavedWorksheets();
    } catch (error) {
      console.error('Error deleting saved worksheet:', error);
      alert(error.response?.data?.detail || 'Failed to delete saved worksheet. Please try again.');
    }
  };

  const deleteTask = async (taskId, taskTitle) => {
    try {
      await axios.delete(`/tasks/${taskId}`);
      alert('Task deleted successfully!');
      
      // Reload tasks to reflect changes
      await loadTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      alert(error.response?.data?.detail || 'Failed to delete task. Please try again.');
    }
  };

  const startEditingTaskTitle = (task) => {
    if (!task?.id) return;
    if (task.owner_id !== user?.id) {
      alert('Only the task creator can edit the title.');
      return;
    }

    setEditingTaskTitleId(task.id);
    setEditingTaskTitleValue(task.title || '');
  };

  const cancelEditingTaskTitle = () => {
    setEditingTaskTitleId(null);
    setEditingTaskTitleValue('');
  };

  const handleEditTaskTitle = async (task, nextTitle = editingTaskTitleValue) => {
    if (!task?.id) return;
    if (task.owner_id !== user?.id) {
      alert('Only the task creator can edit the title.');
      return;
    }

    const trimmedTitle = (nextTitle || '').trim();
    if (!trimmedTitle) {
      alert('Task title cannot be empty.');
      return;
    }

    if (trimmedTitle === task.title) {
      cancelEditingTaskTitle();
      return;
    }

    try {
      await axios.patch(`/tasks/${task.id}/title`, { title: trimmedTitle });
      alert('Task title updated successfully!');
      cancelEditingTaskTitle();
      await loadTasks();
      if (activeView !== 'parent' && activeView !== 'school') {
        const relationship = coachingRelationships.find(rel => rel.id === activeView);
        if (relationship?.coach_id) {
          await loadCoachingTasks(relationship.coach_id);
        }
      }
    } catch (error) {
      console.error('Error updating task title:', error);
      alert(error.response?.data?.detail || 'Failed to update task title. Please try again.');
    }
  };

  const downloadPDF = async () => {
    if (!currentPracticeSheet?.worksheet) return;

    const worksheet = currentPracticeSheet.worksheet;
    const topicsToFormat = worksheet.is_olympiad && worksheet.olympiad_metadata?.topics
      ? worksheet.olympiad_metadata.topics
      : worksheet.topics;

    let generationDate = '';
    if (worksheet.created_at) {
      try {
        const dateObj = new Date(worksheet.created_at);
        generationDate = dateObj.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      } catch (e) {
        generationDate = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      }
    } else {
      generationDate = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    }

    const content = worksheet.html_content_print || worksheet.html_content || '<p>No content available</p>';

    try {
      await saveWorksheetAsPDF({
        title: currentPracticeSheet.task.title,
        content,
        grade: worksheet?.grade,
        subject: worksheet?.subject || currentPracticeSheet.task?.subject,
        topics: topicsToFormat,
        date: generationDate,
        taskCode: currentPracticeSheet.task.task_code,
        totalMarks: worksheet?.total_marks,
        totalQuestions: worksheet?.total_questions,
        fileName: `${currentPracticeSheet.task.title || 'worksheet'}`,
        worksheetId: worksheet?.id,
      });
      
    } catch (error) {
      console.error('Error saving worksheet PDF:', error);
      alert('Unable to save the worksheet as PDF. Please try printing instead.');
    }
  };

  const printPracticeSheet = () => {
    if (currentPracticeSheet) {
      // Format generation date
      let generationDate = '';
      if (currentPracticeSheet.worksheet.created_at) {
        try {
          const dateObj = new Date(currentPracticeSheet.worksheet.created_at);
          generationDate = dateObj.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
        } catch (e) {
          generationDate = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
        }
      } else {
        generationDate = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
      }
      
      // For Olympiad worksheets, check olympiad_metadata for topics
      const topicsToFormat = currentPracticeSheet.worksheet.is_olympiad && currentPracticeSheet.worksheet.olympiad_metadata?.topics
        ? currentPracticeSheet.worksheet.olympiad_metadata.topics
        : currentPracticeSheet.worksheet.topics;
      const formattedTopics = formatTopics(topicsToFormat);
      const topicsStr = formattedTopics;
      console.log(`[Topics] Original: ${currentPracticeSheet.worksheet.topics}, Olympiad metadata: ${currentPracticeSheet.worksheet.olympiad_metadata?.topics}, Final: ${topicsStr}`);

      const printHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${currentPracticeSheet.task.title}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; }
            .meta { font-size: 0.9em; color: #666; margin-top: 5px; }
            .content { line-height: 1.6; }
            .print-footer { margin-top: 28px; padding-top: 12px; border-top: 1px solid #e5e7eb; display: flex; align-items: center; justify-content: center; gap: 8px; color: #6b7280; font-size: 12px; flex-wrap: wrap; text-align: center; }
            .answer-input input, .answer-input textarea { 
              width: 100% !important; 
              border: none !important; 
              border-bottom: 1px solid #000 !important; 
              background: transparent !important; 
              min-height: 2rem !important;
            }
            .answer-input input::placeholder, .answer-input textarea::placeholder { 
              color: transparent !important; 
            }
            @media print { 
              body { margin: 0; } 
              .answer-input { background: white !important; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${currentPracticeSheet.task.title}</h1>
            <p class="meta"><strong>Date:</strong> ${generationDate} | <strong>Grade: </strong>${currentPracticeSheet.worksheet?.grade || 'N/A'} | <strong>Subject:</strong> ${currentPracticeSheet.worksheet?.subject || currentPracticeSheet.task?.subject || 'N/A'}</p>
            <p class="meta"><strong>Topics:</strong> ${topicsStr}</p>
            ${currentPracticeSheet.task.task_code ? 
              `<p class="meta"><strong>Task Code:</strong> ${currentPracticeSheet.task.task_code}</p>` : 
              ''}
          </div>
          <div class="content">
            ${currentPracticeSheet.worksheet?.html_content_print || currentPracticeSheet.worksheet?.html_content || '<p>No content available</p>'}
          </div>
          <div class="print-footer">
            <span>Powered by Edsupp - Your leaning companion</span>
          </div>
        </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      if (printWindow && printWindow.document) {
        printWindow.document.write(printHTML);
        printWindow.document.close();
        const triggerPrint = () => {
          printWindow.focus();
          printWindow.print();
        };
        printWindow.addEventListener('load', triggerPrint, { once: true });
        setTimeout(triggerPrint, 400);
        return;
      }

      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = '0';
      printFrame.style.visibility = 'hidden';
      document.body.appendChild(printFrame);

      const cleanupFrame = () => {
        printFrame.remove();
      };

      printFrame.onload = () => {
        const frameWindow = printFrame.contentWindow;
        if (!frameWindow) {
          console.error('Unable to access print frame window.');
          cleanupFrame();
          return;
        }
        const removeAfterPrint = () => cleanupFrame();
        frameWindow.addEventListener('afterprint', removeAfterPrint, { once: true });
        frameWindow.focus();
        frameWindow.print();
        setTimeout(removeAfterPrint, 1500);
      };

      printFrame.srcdoc = printHTML;

      //const worksheetContent = currentPracticeSheet.worksheet?.html_content_print || currentPracticeSheet.worksheet?.html_content || '<p>No content available</p>';

      //printWorksheet({
      //  title: currentPracticeSheet.task.title,
      //  content: worksheetContent,
      //  grade: currentPracticeSheet.worksheet?.grade,
      //  subject: currentPracticeSheet.worksheet?.subject || currentPracticeSheet.task?.subject,
      //  topics: topicsToFormat,
      //  date: generationDate,
      //  taskCode: currentPracticeSheet.task.task_code,
      //  totalMarks: currentPracticeSheet.worksheet?.total_marks,
      //  totalQuestions: currentPracticeSheet.worksheet?.total_questions,
      //});
    }
  };

  const handleDeleteChild = async () => {
    if (deletingChild) {
      try {
        await axios.delete(`/children/${deletingChild.id}`);
        
        // Refresh children list
        onChildAdded();
        
        // If we deleted the active child, clear selection
        if (activeChild?.id === deletingChild.id) {
          setActiveChild(null);
        }
        
        setShowDeleteConfirm(false);
        setDeletingChild(null);
      } catch (error) {
        console.error('Error deleting child:', error);
        alert('Error deleting child. Please try again.');
      }
    }
  };

  const currentTasksForProgress = getCurrentTasks();
  const filteredCompletedCount = currentTasksForProgress.completed.length;
  const filteredPendingCount = currentTasksForProgress.pending.length;
  const totalFilteredTasks = filteredCompletedCount + filteredPendingCount;
  const completionRate = totalFilteredTasks
    ? Math.round((filteredCompletedCount / totalFilteredTasks) * 100)
    : 0;
  const completionStatus = completionRate >= 80 ? 'On Track' : completionRate >= 50 ? 'Keep Going' : 'Needs Attention';
  const completionBarWidth = totalFilteredTasks ? `${completionRate}%` : '0%';

  return (
    <div className="space-y-4">
      {/* My Children - Only show when no active child or at home */}
      {(!activeChild || currentPage === 'home') && (
        <div id="parent-dashboard-my-children" className="bg-white rounded-lg shadow p-4 sm:p-6">
          <div className="flex justify-between items-center mb-2 sm:mb-4">
            <h2 className="text-lg sm:text-xl font-semibold text-black">👥 My Children</h2>
            <button
              onClick={() => setShowAddChild(true)}
              className="text-xs sm:text-sm bg-blue-600 text-white px-2 py-1 sm:px-4 sm:py-2 rounded-lg hover:bg-blue-700"
              data-testid="add-child-btn"
            >
              <span className="text-xs text-white sm:hidden" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
                </svg>
              </span>
              <span className="hidden sm:inline">Add Child</span>
            </button>
          </div>
        
          {children.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {children.map(child => {
                const isSelected = activeChild?.id === child.id;
                const childFirstName = getFirstName(child.name);
                const childInitial = (childFirstName || child.name || '?').charAt(0);
                return (
                  <div
                    key={child.id}
                    className={`p-2 sm:p-4 rounded-lg border transition-colors transition-all hover:-translate-y-0.5 hover:shadow-md ${
                      isSelected
                        ? 'border-blue-300 bg-gradient-to-r from-green-500 via-blue-600 to-purple-600 text-white shadow-lg'
                        : 'border-gray-200 bg-gray-50 hover:border-gray-300'
                    }`}
                    data-testid={`child-card-${child.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div
                        className="flex flex-1 cursor-pointer items-center space-x-3"
                        onClick={() => setActiveChild(child)}
                      >
                        <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-gradient-to-r from-blue-400 to-blue-600 sm:h-12 sm:w-12">
                          {child.photo_url ? (
                            <img
                              src={child.photo_url.startsWith('/api/') ? `${BACKEND_URL}${child.photo_url}` : child.photo_url}
                              alt={childFirstName || child.name}
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.parentElement.innerHTML = `<span class="text-white font-semibold">${childInitial}</span>`;
                              }}
                            />
                          ) : (
                            <span className="text-white font-semibold">{childInitial}</span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2">
                            <h3 className={`text-sm font-medium sm:text-base ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                              <strong>{childFirstName || child.name}</strong>
                            </h3>
                            {child.access_code && (
                              <span className={`mt-0.5 w-fit rounded-full px-2 py-0.5 text-[11px] font-xs sm:mt-0 sm:py-1 sm:text-xs ${
                                isSelected ? 'bg-white/20 text-white border border-white/30' : 'bg-green-100 text-green-800'
                              }`}>
                                <span className="sm:hidden">{child.access_code}</span>
                                <span className="hidden sm:inline">Code: {child.access_code}</span>
                              </span>
                            )}
                          </div>
                          <p className={`mt-1 hidden text-xs sm:block ${isSelected ? 'text-blue-100' : 'text-gray-600'}`}>
                            Grade {child.grade} • Age {child.age}
                          </p>
                          <p className={`hidden text-xs sm:block ${isSelected ? 'text-blue-100/90' : 'text-gray-500'}`}>
                            {child.school_name} ({child.board})
                          </p>
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex shrink-0 flex-col items-end space-y-0 sm:flex-row sm:items-center sm:space-x-1 sm:space-y-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingChild(child);
                            setShowEditChild(true);
                          }}
                          className="rounded-md p-2 text-gray-400 transition-colors hover:text-blue-600 sm:hover:bg-blue-50 sm:p-2"
                          data-testid={`edit-child-${child.id}`}
                          title="Edit child details"
                        >
                          <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600 mb-2 sm:mb-4">No children added yet</p>
              <button
              onClick={() => setShowAddChild(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Add Your First Child
            </button>
          </div>
        )}
      </div>
      )}

      {activeChild && (
        <>
          {/* Active Child's Learning Space - Above Performance */}
          <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-4 flex items-center">
              <span className="text-lg sm:text-xl mr-2">🎓</span>
              <span className="sm:hidden">{getFirstName(activeChild.name) || activeChild.name}'s Learning Space</span>
              <span className="hidden sm:inline">{getFirstName(activeChild.name) || activeChild.name}'s Learning Space</span>
              {selectedSubject && selectedSubject !== 'All Subjects' && (
                <button
                  onClick={() => setShowViewSyllabus(true)}
                  className="ml-auto shrink-0 inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 sm:px-2 sm:py-2 text-xs font-semibold text-green-700 hover:bg-green-200 transition"
                  data-testid="parent-view-syllabus-btn"
                  title="View Books"
                >
                  <span className="inline sm:hidden">🚀</span>
                  <span className="hidden sm:inline">🚀 Start Learning</span>
                </button>
              )}
            </h2>
            
            {/* Space Tabs */}
            <div className="mb-2 sm:mb-4">
              <div className="flex flex-nowrap gap-2 overflow-x-auto no-scrollbar bg-gradient-to-r from-teal-50 via-sky-50 to-purple-50 rounded-lg p-2 w-full sm:w-fit">
                <button
                  onClick={() => setActiveView('parent')}
                  className={`shrink-0 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold shadow-sm transition transform hover:-translate-y-0.5 whitespace-nowrap ${
                    activeView === 'parent'
                      ? 'bg-gradient-to-r from-teal-500 to-purple-600 text-white'
                      : 'bg-white text-sky-700 border border-sky-100 hover:border-sky-300'
                  }`}
                  data-testid="parent-space-tab"
                >
                  Parent Space
                  {(() => {
                    const parentPendingCount = pendingTasks.length;
                    return parentPendingCount > 0 && (
                      <span
                        className={`ml-2 px-1.5 py-0.5 text-xs font-bold rounded-full ${
                          activeView === 'parent'
                            ? 'bg-white text-blue-600'
                            : 'bg-red-500 text-white'
                        }`}
                      >
                        {parentPendingCount}
                      </span>
                    );
                  })()}
                </button>
                {hasSchoolLink && (
                  <button
                    onClick={() => setActiveView('school')}
                    className={`shrink-0 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold shadow-sm transition transform hover:-translate-y-0.5 whitespace-nowrap ${
                        activeView === 'school'
                        ? 'bg-gradient-to-r from-teal-500 to-purple-600 text-white'
                        : 'bg-white text-sky-700 border border-sky-100 hover:border-sky-300'
                    }`}
                    data-testid="school-space-tab"
                  >
                    School Space
                    {(() => {
                      const schoolPendingCount = schoolTasks.pending.length;
                      return schoolPendingCount > 0 && (
                        <span
                          className={`ml-2 px-1.5 py-0.5 text-xs font-bold rounded-full ${
                            activeView === 'school'
                              ? 'bg-white text-blue-600'
                              : 'bg-red-500 text-white'
                          }`}
                        >
                          {schoolPendingCount}
                        </span>
                      );
                    })()}
                  </button>
                )}
                {/* Add buttons for each coaching relationship - grouped by coach */}
                {activeChild && (() => {
                  const coachingTeacherRelationships = coachingRelationships.filter(
                    rel =>
                      rel.coach_details?.role !== 'school_teacher' &&
                      rel.coach_details?.name &&
                      rel.coach_id
                  );
                  // Filter relationships for active child
                  const childRelationships = coachingTeacherRelationships.filter(
                    rel => rel.child_details?.id === activeChild.id
                  );
                  
                  // Group relationships by coach_id
                  const groupedByCoach = {};
                  childRelationships.forEach(rel => {
                    if (!groupedByCoach[rel.coach_id]) {
                      groupedByCoach[rel.coach_id] = {
                        coach_id: rel.coach_id,
                        coach_details: rel.coach_details,
                        subjects: [],
                        relationshipIds: []
                      };
                    }
                    groupedByCoach[rel.coach_id].subjects.push(rel.subject);
                    groupedByCoach[rel.coach_id].relationshipIds.push(rel.id);
                  });
                  
                  // Convert to array and render
                  return Object.values(groupedByCoach).map(group => (
                    <button
                      key={group.coach_id}
                      onClick={() => setActiveView(group.relationshipIds[0])} // Use first relationship ID
                      className={`shrink-0 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl text-xs sm:text-sm font-semibold shadow-sm transition transform hover:-translate-y-0.5 whitespace-nowrap ${
                        group.relationshipIds.includes(activeView)
                          ? 'bg-gradient-to-r from-teal-500 to-purple-600 text-white'
                          : 'bg-white text-sky-700 border border-sky-100 hover:border-sky-300'
                      }`}
                      data-testid={`coaching-space-tab-${group.coach_id}`}
                    >
                      Teacher {(group.coach_details?.name || '').trim().split(/\s+/)[0]}
                      {(() => {
                        const coachPendingCount = pendingTasksByOwner[group.coach_id] || 0;
                        return coachPendingCount > 0 && (
                          <span
                            className={`ml-2 px-1.5 py-0.5 text-xs font-bold rounded-full ${
                              group.relationshipIds.includes(activeView)
                                ? 'bg-white text-blue-600'
                                : 'bg-red-500 text-white'
                            }`}
                          >
                            {coachPendingCount}
                          </span>
                        );
                      })()}
                    </button>
                  ));
                })()}
              </div>
              {coachingRequests.length > 0 && (
                <div className="mt-2 hidden sm:block">
                  <button
                    onClick={() => setShowCoachingRequests(true)}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                    {coachingRequests.length} pending coaching request(s)
                  </button>
                </div>
              )}
            <p className="hidden text-xs text-gray-500 mt-1">
              {activeChild &&
              coachingRelationships.some(
                rel => rel.child_details?.id === activeChild.id && rel.coach_details?.role !== 'school_teacher'
              )
                ? `Teacher linked for ${getFirstName(activeChild.name) || activeChild.name}`
                : activeChild
                  ? `No teacher linked for ${getFirstName(activeChild.name) || activeChild.name}`
                  : 'Select a child to view coaching status'
              }
            </p>
            </div>

            {/* Subject Pills */}
            <div className="mb-1 sm:mb-1">
              <div className="mb-1 sm:mb-1 flex items-center justify-between gap-2">
                <h3 className="text-sm font-medium text-gray-700">
                  <span className="hidden">Subjects for Grade {activeChild.grade} ({activeChild.board})</span>
                </h3>
              </div>
              {/*<div className="flex flex-wrap gap-2">*/}
              <div className="flex flex-nowrap gap-2 overflow-x-auto no-scrollbar py-1">
                {(() => {
                  const displayedSubjects = getSubjectsForActiveView();
                  
                  return displayedSubjects.map((subject) => (
                    <button
                      key={subject}
                      onClick={() => setSelectedSubject(subject)}
                      className={`shrink-0 px-3 py-1.5 text-xs sm:text-sm rounded-full border transition-all relative whitespace-nowrap ${
                        selectedSubject === subject
                          ? 'bg-gradient-to-r from-emerald-400 to-sky-500 text-white border-transparent shadow-md'
                          : 'border-sky-200 text-sky-700 bg-white/70 hover:border-sky-300 hover:-translate-y-0.5'
                      }`}
                      data-testid={`subject-pill-${subject.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {subject}
                      {(() => {
                        const filteredCounts = getFilteredTaskCounts();
                        const count = filteredCounts[subject] || 0;
                        return count > 0 && (
                          <span className={`ml-2 px-1.5 py-0.5 text-[10px] sm:text-xs font-bold rounded-full ${
                            selectedSubject === subject 
                              ? 'bg-white text-blue-600' 
                              : 'bg-red-500 text-white'
                          }`}>
                            {count}
                          </span>
                        );
                      })()}
                    </button>
                  ));
                })()}
              </div>
              {getSubjectsForActiveView().length === 0 && (
                <p className="text-gray-500 text-sm">
                  <span className="sm:hidden">Loading subjects...</span>
                  <span className="hidden sm:inline">Loading subjects for {activeChild.board} Grade {activeChild.grade}...</span>
                </p>
              )}
            </div>

            {/* Grade Filter Pills — hidden in Coaching Teacher space, where a
                batch's grade can legitimately differ from the child's own
                profile grade. */}
            {(activeView === 'parent' || activeView === 'school') && parentAvailableGrades.length > 1 && (
              <div className="mt-2">
                <div className="flex flex-nowrap gap-2 overflow-x-auto no-scrollbar py-2 sm:py-4">
                  {getAvailableGradeFilters().map((grade) => (
                    <button
                      key={grade}
                      type="button"
                      onClick={() => setParentGradeFilter(grade)}
                      className={`shrink-0 px-3 py-1.5 text-xs sm:text-sm rounded-full border transition-all relative whitespace-nowrap ${parentGradeFilter === grade ?
                        'bg-gradient-to-r from-purple-400 to-sky-500 text-white border-transparent shadow-md'
                        : 'border-sky-200 text-sky-700 bg-white/70 hover:border-sky-300 hover:-translate-y-0.5'}`}
                    >
                      {formatGradeLabel(grade)}
                      {(() => {
                        const gradeCounts = getGradeTaskCounts();
                        const count = gradeCounts[grade] || 0;
                        return count > 0 && (
                          <span className={`ml-2 px-1.5 py-0.5 text-[10px] sm:text-xs font-bold rounded-full ${
                            parentGradeFilter === grade
                              ? 'bg-white text-purple-600'
                              : 'bg-red-500 text-white'
                          }`}>
                            {count}
                          </span>
                        );
                      })()}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div id="parent-dashboard-quick-actions" className="bg-white rounded-lg shadow p-4 sm:p-6 scroll-mt-24">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-4">🚀 Quick Actions for {getFirstName(activeChild.name) || activeChild.name}</h2>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              <button
                onClick={() => {
                  if (openTaskSelectionPage) {
                    openTaskSelectionPage();
                  } else {
                    setShowTaskSelection(true);
                  }
                }}
                className="p-3 sm:p-4 text-center rounded-xl border-2 border-blue-300 bg-gradient-to-br from-blue-100 to-blue-100 hover:from-blue-200 hover:to-blue-200 transition-all hover:-translate-y-0.5 hover:shadow-md"
                data-testid="create-task-btn"
              >
                <div className="text-3xl mb-2">📝</div>
                <div className="text-xs sm:text-sm font-bold text-blue-700">Create Task</div>
                <p className="mt-1 hidden text-xs text-blue-600 sm:block">Sheet, Notes or Video</p>
              </button>
              
              <button
                onClick={() => setShowViewSyllabus(true)}
                className="p-3 sm:p-4 text-center rounded-xl border-2 border-green-300 bg-gradient-to-br from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 transition-all hover:-translate-y-0.5 hover:shadow-md"
                data-testid="view-syllabus-btn"
              >
                <div className="text-3xl mb-2">📘</div>
                <div className="text-xs sm:text-sm font-bold text-green-700">View Books</div>
                <p className="mt-1 hidden text-xs text-green-600 sm:block">Select syllabus</p>
              </button>
              
              {/*<button
                onClick={() => setShowWorksheetGenerator(true)}
                className="p-3 sm:p-4 text-center rounded-xl border-2 border-blue-300 bg-gradient-to-br from-blue-100 to-blue-100 hover:from-blue-200 hover:to-blue-200 transition-all hover:-translate-y-0.5 hover:shadow-md"
                data-testid="create-sheet-btn"
              >
                <div className="text-3xl mb-2">📝</div>
                <div className="text-xs sm:text-sm font-bold text-blue-700">Create Sheet</div>
                <p className="mt-1 hidden text-xs text-blue-600 sm:block">Generate worksheet</p>
              </button>*/}
              
              <button
                onClick={() => setShowSubmitWork('upload')}
                className="p-3 sm:p-4 text-center rounded-xl border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-red-100 hover:from-pink-100 hover:to-red-100 transition-all hover:-translate-y-0.5 hover:shadow-md"
                data-testid="submit-work-btn"
              >
                <div className="text-3xl mb-2">📤</div>
                <div className="text-xs sm:text-sm font-bold text-red-700">Submit Work</div>
                <p className="mt-1 hidden text-xs text-red-600 sm:block">Upload answers</p>
              </button>

              <button
                onClick={() => {
                  if (openChildInsightsPage) {
                    openChildInsightsPage();
                  } else {
                    setShowChildInsights(true);
                  }
                }}
                className="p-3 sm:p-4 text-center rounded-xl border-2 border-sky-300 bg-gradient-to-br from-sky-100 to-sky-100 hover:from-sky-100 hover:to-sky-200 transition-all hover:-translate-y-0.5 hover:shadow-md"
                data-testid="child-insights-btn"
              >
                <div className="text-3xl mb-2">📊</div>
                <div className="text-xs sm:text-sm font-bold text-sky-700">Child Insights</div>
                <p className="mt-1 hidden text-xs text-sky-600 sm:block">View progress</p>
              </button>
              
              {/*<button
                onClick={openDefaultNotesGenerator}
                className="p-3 sm:p-4 text-center rounded-xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-purple-50 hover:from-purple-100 hover:to-purple-100 transition-all hover:-translate-y-0.5 hover:shadow-md"
                data-testid="create-notes-btn"
              >
                <div className="text-3xl mb-2">📒</div>
                <div className="text-xs sm:text-sm font-bold text-indigo-700">Create Notes</div>
                <p className="mt-1 hidden text-xs text-indigo-600 sm:block">Open notes generator</p>
              </button>

              <button
                onClick={() => setShowVideoCreator(true)}
                className="p-3 sm:p-4 text-center rounded-xl border-2 border-yellow-300 bg-gradient-to-br from-yellow-50 to-yellow-100 hover:from-yellow-100 hover:to-yellow-200 transition-all hover:-translate-y-0.5 hover:shadow-md"
                data-testid="create-video-btn"
              >
                <div className="text-3xl mb-2">🎬</div>
                <div className="text-xs sm:text-sm font-bold text-yellow-800">Create Video</div>
                <p className="mt-1 hidden text-xs text-yellow-700 sm:block">Teaching slides</p>
              </button>*/}
              
              <button
                onClick={() => setShowMyDocs(true)}
                className="p-3 sm:p-4 text-center rounded-xl border-2 border-yellow-300 bg-gradient-to-br from-yellow-50 to-yellow-100 hover:from-yellow-100 hover:to-yellow-200 transition-all hover:-translate-y-0.5 hover:shadow-md"
                data-testid="my-docs-btn"
              >
                <div className="text-3xl mb-2">📁</div>
                <div className="text-xs sm:text-sm font-bold text-yellow-700">My Docs</div>
                <p className="mt-1 hidden text-xs text-yellow-600 sm:block">Manage uploads</p>
              </button>

              <button
                onClick={() => setShowOlympiadModal(true)}
                className="p-3 sm:p-4 text-center rounded-xl border-2 border-orange-300 bg-gradient-to-br from-orange-100 to-rose-100 hover:from-orange-200 hover:to-rose-200 transition-all hover:-translate-y-0.5 hover:shadow-md"
                data-testid="olympiad-btn"
              >
                <div className="text-3xl mb-2">🏆</div>
                <div className="text-xs sm:text-sm font-bold text-orange-800">Olympiad</div>
                <p className="mt-1 hidden text-xs text-orange-700 sm:block">Competitive prep</p>
              </button>
            </div>
          </div>

          {/* Child Performance Overview - Below Learning Space */}
          <div id="parent-dashboard-performance" className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sm:p-6 scroll-mt-24">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2 sm:mb-4">📊 {getFirstName(activeChild.name) || activeChild.name}'s Performance</h2>
            {childPerformance ? (
              <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/40">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <div className="text-sm font-semibold text-gray-800 dark:text-gray-200">Progress Meter</div>
                  </div>
                  <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 border border-blue-100 dark:border-blue-800 rounded-full overflow-hidden mb-2">
                    <div
                      className="h-full bg-blue-500 transition-all"
                      style={{ width: completionBarWidth }}
                    ></div>
                  </div>
                  <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">✅</span>
                        <div>
                          <p className="text-base font-semibold text-green-600 dark:text-green-400">{filteredCompletedCount}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🟠</span>
                        <div>
                          <p className="text-base font-semibold text-orange-600 dark:text-orange-400">{filteredPendingCount}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="text-left">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Overall</p>
                        <p className="text-base font-semibold text-gray-900 dark:text-gray-100">{completionRate}%</p>
                      </div>
                      <span className="text-center text-[10px] sm:text-xs text-gray-700 dark:text-gray-200 gap-3 px-1 py-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full">{completionStatus}</span>
                    </div>
                  </div>
                </div>
                
                <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/40 border border-purple-200 dark:border-purple-700 rounded-lg">
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mb-2 sm:mb-4">
                    {Math.round(childPerformance.average_score || 0)}%
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">Proficiency Score</div>
                  <div className="mt-1">
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                      <div
                        className="bg-purple-600 h-1.5 rounded-full"
                        style={{width: `${childPerformance.average_score || 0}%`}}
                      ></div>
                    </div>
                  </div>
                </div>
                
                <div className="hidden sm:inline text-center p-4 bg-green-50 dark:bg-green-900/40 border border-green-200 dark:border-green-700 rounded-lg">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400 mb-2 sm:mb-4">
                    {childPerformance.rank || 'Top 25%'}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-300">Class Rank</div>
                  <div className="mt-1">
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                      <div className="bg-green-600 h-1.5 rounded-full" style={{width: '75%'}}></div>
                    </div>
                  </div>
                </div>
                
                <div className="hidden sm:inline text-center p-4 bg-amber-50 dark:bg-amber-900/40 border border-amber-200 dark:border-amber-700 rounded-lg">
                  <div className="text-xl mb-2 sm:mb-4">😊</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Good</div>
                  <div className="text-xs text-gray-600 dark:text-gray-300">Emotional Well-being</div>
                  <div className="mt-1 flex justify-center space-x-1">
                    {[1,2,3,4,5].map(i => (
                      <div 
                        key={i}
                        className={`w-2 h-2 rounded-full ${i <= 4 ? 'bg-yellow-400' : 'bg-gray-300 dark:bg-gray-600'}`}
                      ></div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => {
                    if (openChildInsightsPage) {
                      openChildInsightsPage();
                    } else {
                      setShowChildInsights(true);
                    }
                  }}
                  className="px-3 py-2 text-blue-700 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-200 bg-indigo-50 dark:bg-indigo-900/50 hover:bg-indigo-100 dark:hover:bg-indigo-900/70 transition-all rounded flex items-center justify-center space-x-2 hover:shadow-lg"
                >
                  <span className="font-medium hover:underline text-xs sm:text-sm">View Detailed Insights</span>
                  <span>→</span>
                </button>
              </div>
              </>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Loading performance data...
              </div>
            )}
          </div>

          {/* Task View Toggle (Active/Archive) */}
          <div className="flex justify-end items-center mb-2 sm:mb-4 space-x-4">
            <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
                <button
                  onClick={() => setShowTaskFilters(!showTaskFilters)}
                  className="px-4 py-2 bg-gray-100 text-xs sm:text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
                >
                  <span>🔍</span>
                  <span>{showTaskFilters ? '▲' : '▼'}</span>
                </button>
              <button
                onClick={() => setTaskView('active')}
                className={`px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                  taskView === 'active'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => {
                  setTaskView('archive');
                  if (archivedTasks.length === 0) {
                    loadArchivedTasksForParent();
                  }
                }}
                className={`px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                  taskView === 'archive'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                Archive
              </button>
              {activeView === 'parent' && (
                <button
                  onClick={() => {
                    setTaskView('saved');
                    setShowTaskFilters(false);
                  }}
                  className={`px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                    taskView === 'saved'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  Saved
                </button>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  loadChildEvaluationsForParent();
                  loadTasks();
                }}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>

          {taskView === 'active' && showTaskFilters && (
            <div className="bg-white rounded-xl shadow-sm p-4 mb-2 sm:mb-4">
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Filter by Task Title
                  </label>
                  <input
                    type="text"
                    value={parentTaskTitleFilter}
                    onChange={(e) => setParentTaskTitleFilter(e.target.value)}
                    placeholder="Enter task title..."
                    className="w-full px-2 py-1 sm:px-3 sm:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Tasks */}
          {taskView === 'active' ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pending Tasks - Left */}
            {(() => {
              const allPending = getCurrentTasks().pending;
              const filteredPending = filterParentTasks(allPending);
              return (
            <div id="parent-dashboard-pending-tasks" className="bg-white rounded-xl shadow-sm p-4 sm:p-6 flex flex-col scroll-mt-24">
              <div className="flex items-center justify-between mb-2 sm:mb-4 flex-shrink-0">
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-4">
                    🟠 {(getFirstName(activeChild.name) || activeChild.name)}'s Pending Tasks
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedSubject && selectedSubject !== 'All Subjects' && (
                      <span className="font-medium">
                        {selectedSubject}
                        {(activeView === 'parent' || activeView === 'school') && parentGradeFilter ? ` • Grade: ${parentGradeFilter}` : ''}
                      </span>
                    )}
                  </p>
                </div>
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                  {filteredPending.length}{allPending.length !== filteredPending.length} pending
                </span>
              </div>
              <div className="space-y-3 min-h-[50px] max-h-[200px] overflow-y-auto" data-testid="pending-tasks">
                {filteredPending.map(task => {
                  const isParentCreatedTask = task.owner_id === user?.id && task.assigned_by_role !== 'school_teacher';
                  const isOlympiadTask = task.is_olympiad === true;

                  // Find child names for this task
                  const taskChildren = task.target_ids ? task.target_ids.map(childId => {
                    const child = children.find(c => c.id === childId);
                    const childFirstName = getFirstName(child?.name);
                    return child ? (childFirstName || child.name) : 'Unknown Child';
                  }).join(', ') : 'No assigned child';
                  
                  return (
                    <div key={task.id} className={`p-4 border-l-4 rounded-lg shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md
                    ${isChallengeTask(task) 
                          ? 'border-purple-400 bg-gradient-to-r from-purple-100 to-purple-100' :
                          isOlympiadTask ? 
                          'border-orange-500 bg-gradient-to-r from-orange-100 to-orange-100'
                          : 'border-amber-400 bg-gradient-to-r from-amber-50 to-amber-50'
                      }`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            {editingTaskTitleId === task.id ? (
                              <>
                                <input
                                  type="text"
                                  value={editingTaskTitleValue}
                                  onChange={(e) => setEditingTaskTitleValue(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-sm px-2 py-1 border border-amber-300 rounded"
                                  maxLength={200}
                                  autoFocus
                                />
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditTaskTitle(task, editingTaskTitleValue);
                                  }}
                                  className="compact-action-btn text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded hover:bg-green-200"
                                  title="Save task title"
                                >
                                  ✅
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    cancelEditingTaskTitle();
                                  }}
                                  className="compact-action-btn text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                                  title="Cancel editing"
                                >
                                  ❌
                                </button>
                              </>
                            ) : (
                              <>
                                <h4 className="font-semibold text-gray-900" title={task.title}>{truncateTaskTitle(task.title)}</h4>
                                {task.owner_id === user?.id && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      startEditingTaskTitle(task);
                                    }}
                                    className="compact-action-btn text-[10px] px-1.5 py-0.5 text-amber-700 rounded hover:bg-amber-200"
                                    title="Edit task title"
                                  >
                                    ✏️
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                          {/*<p className="text-sm text-blue-600 mb-1">👤 {taskChildren}</p>*/}
                          {isOlympiadTask && (
                            <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full bg-orange-200 text-orange-800 mb-1">
                              🏆 Olympiad
                            </span>
                          )}
                          {isChallengeTask(task) && (
                            <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 text-white mb-1">
                              Assigned by Edsupp
                            </span>
                          )}
                          {isTaskOverdue(task) && (
                            <span
                              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 mb-1"
                              title={`Due ${formatTaskDueDateTime(task.due_date)}`}
                            >
                              🚩 Overdue
                            </span>
                          )}
                          <div className="flex flex-wrap items-center text-xs text-gray-600 gap-2 whitespace-nowrap mt-2">
                            <span>• Created {new Date(task.created_at).toLocaleDateString()}</span>
                            {task.due_date && (
                              <span
                                className={`px-2 py-1 rounded-full inline-flex items-center gap-1 whitespace-nowrap ${
                                  isTaskOverdue(task) ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'
                                }`}
                                title={`Due ${formatTaskDueDateTime(task.due_date)}`}
                              >
                                ⏰ Due {formatTaskDueDateTime(task.due_date)}
                              </span>
                            )}
                          </div>
                        </div>
                          </div>
                          <div className="flex flex-wrap gap-2 mt-3 pt-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              viewPracticeSheet(task.id);
                            }}
                            className="text-[10px] sm:text-xs px-2 py-1 sm:px-2 sm:py-2 border border-green-300 bg-green-100 text-green-700 rounded hover:bg-green-200"
                            title="View Practice Sheet"
                          >
                            <span className="sm:hidden">📄 Sheet</span>
                            <span className="hidden sm:inline">📄 Sheet</span>
                          </button>
                          {/*{isParentCreatedTask && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                viewAnswerKey(task.id);
                              }}
                              className="text-[10px] sm:text-xs px-2 py-1 sm:px-2 sm:py-2 border border-blue-300 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                              title="View Answer Key"
                            >
                              <span className="sm:hidden">Answer</span>
                              <span className="hidden sm:inline">📋 Answer</span>
                            </button>
                          )}*/}
                          {isParentCreatedTask && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditAssignedWorksheet(task);
                              }}
                              disabled={editingTaskWorksheetId === task.id}
                              className={`text-[10px] sm:text-xs px-2 py-1 sm:px-2 sm:py-2 rounded ${
                                editingTaskWorksheetId === task.id
                                  ? 'border border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                                  : 'border border-orange-300 bg-orange-100 text-orange-700 hover:bg-orange-200'
                              }`}
                              title="Edit Sheet"
                            >
                              {editingTaskWorksheetId === task.id ? '⏳ Opening...' : '✏️ Edit'}
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTaskForUpload(task);
                            }}
                            className="text-[10px] sm:text-xs px-2 py-1 sm:px-2 sm:py-2 border border-purple-300 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                            title="Upload Work"
                          >
                            <span className="sm:hidden">📤 Submit</span>
                            <span className="hidden sm:inline">📤 Submit</span>
                          </button>
                          {isParentCreatedTask && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setPendingDeleteTask({ id: task.id, title: task.title });
                                setShowDeleteTaskConfirm(true);
                              }}
                              className="text-[10px] sm:text-xs px-2 py-1 sm:px-2 sm:py-2 border border-red-300 bg-red-100 text-red-700 rounded hover:bg-red-200"
                              title="Delete Task"
                            >
                              <span className="sm:hidden">🗑️ Delete</span>
                              <span className="hidden sm:inline">🗑️ Delete</span>
                            </button>
                          )}
                      </div>
                    </div>
                  );
                })}
                {!loadingChildData && filteredPending.length === 0 && (
                  <div className="bg-blue-50 text-center py-8">
                    <div className="text-4xl mb-2 sm:mb-4">✅</div>
                    <p className="text-gray-500">No pending tasks</p>
                    <p className="text-sm text-gray-400 mt-1">Tasks will appear here after they are assigned</p>
                  </div>
                )}
              </div>
            </div>
              );
            })()}

            {/* Completed Tasks - Right */}
            {(() => {
              const allCompleted = getCurrentTasks().completed;
              const filteredCompleted = filterParentTasks(allCompleted);
              return (
            <div id="parent-dashboard-completed-tasks" className="bg-white rounded-xl shadow-sm p-4 sm:p-6 flex flex-col scroll-mt-24">
              <div className="flex items-center justify-between mb-2 sm:mb-4 flex-shrink-0">
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2 sm:mb-4">
                    ✅ {(getFirstName(activeChild.name) || activeChild.name)}'s Completed Tasks
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {selectedSubject && selectedSubject !== 'All Subjects' && (
                      <span className="font-medium">
                        {selectedSubject}
                        {(activeView === 'parent' || activeView === 'school') && parentGradeFilter ? ` • Grade: ${parentGradeFilter}` : ''}
                      </span>
                    )}
                  </p>
                </div>
                <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                  {filteredCompleted.length}{allCompleted.length !== filteredCompleted.length} done
                </span>
              </div>
              <div className="space-y-4 min-h-[50px] max-h-[200px] overflow-y-auto" data-testid="completed-tasks">
                {loadingChildData ? (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-2 sm:mb-4"></div>
                    <p className="text-gray-600">Loading evaluations...</p>
                  </div>
                ) : filteredCompleted.map(task => {
                  // Find child names for this task
                  const taskChildren = task.target_ids ? task.target_ids.map(childId => {
                    const child = children.find(c => c.id === childId);
                    const childFirstName = getFirstName(child?.name);
                    return child ? (childFirstName || child.name) : 'Unknown Child';
                  }).join(', ') : 'No assigned child';
                  
                  const evaluation = getCurrentTasks().evaluations[task.id];
                  const isOpen = expandedParentCompletedTasks[task.id];
                  const isSchoolSpaceTask = activeView === 'school' || task.assigned_by_role === 'school_teacher';
                  const isOlympiadTask = task.is_olympiad === true;
                  const canReevaluate = evaluation?.ai_based === true && !isSchoolSpaceTask;
                  console.log(`[PARENT TASK DISPLAY] Task ${task.id} (${task.title}):`, evaluation ? 'HAS evaluation' : 'NO evaluation');
                  console.log(`[PARENT TASK DISPLAY] Available evaluations:`, Object.keys(getCurrentTasks().evaluations));
                  
                  return (
                    <div 
                      key={task.id} 
                      className="p-4 border-l-4 border-green-500 bg-gradient-to-r from-cyan-50 to-green-50 rounded-lg transition-all hover:-translate-y-0.5 hover:shadow-md cursor-pointer"
                      onClick={() => {
                        if (evaluation) {
                          viewTaskEvaluationAsParent(task.id);
                        }
                      }}
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0 pr-2">
                          <div className="flex items-center gap-2">
                          <div className="flex items-center gap-2">
                            {editingTaskTitleId === task.id ? (
                              <>
                                <input
                                  type="text"
                                  value={editingTaskTitleValue}
                                  onChange={(e) => setEditingTaskTitleValue(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-sm px-2 py-1 border border-amber-300 rounded"
                                  maxLength={200}
                                  autoFocus
                                />
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditTaskTitle(task, editingTaskTitleValue);
                                  }}
                                  className="compact-action-btn text-[10px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded hover:bg-green-200"
                                  title="Save task title"
                                >
                                  ✅
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    cancelEditingTaskTitle();
                                  }}
                                  className="compact-action-btn text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                                  title="Cancel editing"
                                >
                                  ❌
                                </button>
                              </>
                            ) : (
                              <>
                                <h4 className="font-medium text-gray-900" title={task.title}><strong>{truncateTaskTitle(task.title)}</strong></h4>
                                {task.owner_id === user?.id && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      startEditingTaskTitle(task);
                                    }}
                                    className="compact-action-btn text-[10px] px-1.5 py-0.5 text-amber-700 rounded hover:bg-amber-200"
                                    title="Edit task title"
                                  >
                                    ✏️
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                          {/*<p className="text-sm text-blue-600 mb-1">👤 {taskChildren}</p>*/}
                          {isOlympiadTask && (
                            <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full bg-orange-200 text-orange-800 mb-1">
                              🏆 Olympiad
                            </span>
                          )}
                          {isChallengeTask(task) && (
                            <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 text-white mb-1">
                              Assigned by Edsupp
                            </span>
                          )}
                          {task.due_date && (
                            <span
                              className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full mb-1 whitespace-nowrap ${
                                isTaskOverdue(task) ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-800'
                              }`}
                              title={`Due ${formatTaskDueDateTime(task.due_date)}`}
                            >
                              ⏰ Due {formatTaskDueDateTime(task.due_date)}
                            </span>
                          )}
                          {isTaskSubmittedLate(task) && (
                            <span
                              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 mb-1"
                              title={`Submitted ${new Date(task.submitted_at).toLocaleString()} (after due date)`}
                            >
                              🚩 Late
                            </span>
                          )}
                          <div className="flex items-center text-xs text-gray-600 gap-2 mt-2">
                            <span>• Created {new Date(task.created_at).toLocaleDateString()}</span>
                            {task.submitted_at && (
                              <span>• Submitted {new Date(task.submitted_at).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end text-right space-y-1 shrink-0">
                          {evaluation && (
                            <div className="text-right">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <span className={`px-2 py-1 text-xs font-bold rounded ${
                                  evaluation.overall_score >= 90 ? 'bg-green-100 text-green-800' :
                                  evaluation.overall_score >= 80 ? 'bg-blue-100 text-blue-800' :
                                  evaluation.overall_score >= 70 ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {evaluation.grade}
                                </span>
                                <span className="text-lg font-bold text-gray-900">{evaluation.overall_score}%</span>
                              </div>
                              <p className="text-xs text-gray-500">Click for insights</p>
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleParentCompletedTask(task.id);
                            }}
                            className="text-xs text-blue-600 font-semibold"
                            >
                              {isOpen ? '▲ Hide' : '▼ Show'}
                          </button>
                        </div>
                      </div>
                      
                      {isOpen && (
                        <>
                          {evaluation ? (
                            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                <div>
                                  <span className="font-medium text-green-700">Strengths:</span>
                                  <p className="text-gray-600 mt-1">
                                    {Array.isArray(evaluation.strengths)
                                      ? evaluation.strengths.slice(0, 2).join(', ') + (evaluation.strengths.length > 2 ? '...' : '')
                                      : (evaluation.strengths?.substring(0, 80) || 'N/A') + '...'}
                                  </p>
                                </div>
                                <div>
                                  <span className="font-medium text-blue-700">Learning Focus:</span>
                                  <p className="text-gray-600 mt-1">
                                    {Array.isArray(evaluation.weaknesses)
                                      ? evaluation.weaknesses.slice(0, 2).join(', ') + (evaluation.weaknesses.length > 2 ? '...' : '')
                                      : Array.isArray(evaluation.areas_for_improvement)
                                        ? evaluation.areas_for_improvement.slice(0, 2).join(', ')
                                        : (evaluation.areas_for_improvement?.substring(0, 80) || 'N/A') + '...'}
                                  </p>
                                </div>
                              </div>

                              {/* Always show Full Analysis link for all evaluations */}
                              <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-200 gap-3">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    viewTaskEvaluationAsParent(task.id);
                                  }}
                                  className="text-[10px] sm:text-xs px-2 py-1 border border-blue-300 bg-blue-100 text-blue-600 rounded hover:underline text-left"
                                >
                                  📈 Full Analysis →
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCurrentParentEvaluation(evaluation);
                                    setShowPersonalizedWorksheetModal(true);
                                  }}
                                  className="text-[10px] sm:text-xs px-2 py-1 border border-purple-300 bg-purple-50 text-purple-700 rounded hover:bg-purple-100 font-medium"
                                >
                                  🎯 Personalized Sheet
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-2 p-2 bg-yellow-50 rounded text-xs text-yellow-700 animate-pulse">
                              <div className="flex items-center space-x-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
                                <span>📊 Evaluation in progress... This may take 30-60 seconds</span>
                              </div>
                              <div className="mt-1 text-xs text-yellow-600">
                                The task will refresh automatically when complete
                              </div>
                            </div>
                          )}

                          {/* Action buttons */}
                          <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center">
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  viewComparison(task.id);
                                }}
                                className="text-[10px] sm:text-xs px-1 py-1 border border-sky-300 bg-sky-100 text-sky-700 rounded hover:bg-sky-200"
                                title="View Answer Comparison"
                              >
                                <span className="sm:hidden">Compare</span>
                                <span className="hidden sm:inline">📊 Compare</span>
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  viewPracticeSheet(task.id);
                                }}
                                className="text-[10px] sm:text-xs px-1 py-1 border border-green-300 bg-green-100 text-green-700 rounded hover:bg-green-200"
                                title="View Practice Sheet"
                              >
                                <span className="sm:hidden">Sheet</span>
                                <span className="hidden sm:inline">📄 Sheet</span>
                              </button>
                              {/*<button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  viewAnswerKey(task.id);
                                }}
                                className="text-[10px] sm:text-xs px-1 py-1 border border-blue-300 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                                title="View Answer Key"
                              >
                                📋 Answer
                              </button>*/}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  reEvaluateTask(task.id);
                                }}
                                disabled={!canReevaluate}
                                className={`text-[10px] sm:text-xs px-1 py-1 rounded ${
                                  canReevaluate
                                    ? 'border border-amber-300 bg-amber-100 text-amber-700 hover:bg-amber-200'
                                    : 'border border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                                }`}
                                title={
                                  canReevaluate
                                    ? 'Re-evaluate'
                                    : isSchoolSpaceTask
                                      ? 'Only school teachers can re-evaluate School Space tasks'
                                      : 'Re-evaluation is not available for this task'
                                }
                              >
                                <span className="sm:hidden">Re-eval</span>
                                <span className="hidden sm:inline">♻️ Re-evaluate</span>
                                
                              </button>
                              {/* Hide Reassign button for General Evaluation tasks */}
                              {!task.is_general_evaluation && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (!isSchoolSpaceTask) {
                                      setPendingReassignTaskId(task.id);
                                      setShowReassignConfirm(true);
                                    }
                                  }}
                                  disabled={isSchoolSpaceTask}
                                  className={`text-[10px] sm:text-xs px-1 py-1 rounded ${
                                    isSchoolSpaceTask
                                      ? 'border border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                                      : 'border border-orange-300 bg-orange-100 text-orange-700 hover:bg-orange-200'
                                  }`}
                                  title={
                                    isSchoolSpaceTask
                                      ? 'Only school teachers can reassign School Space tasks'
                                      : 'Reassign Task'
                                  }
                                >
                                  <span className="sm:hidden">Reassign</span>
                                  <span className="hidden sm:inline">🔄 Reassign</span>
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPendingArchiveTaskId(task.id);
                                  setShowArchiveConfirm(true);
                                }}
                                className="text-[10px] sm:text-xs px-1 py-1 border border-gray-300 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                                title="Archive Task"
                              >
                                  <span className="sm:hidden">Archive</span>
                                  <span className="hidden sm:inline">💼 Archive</span>
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
                {!loadingChildData && filteredCompleted.length === 0 && (
                  <div className="text-center bg-pink-50 py-8">
                    <div className="text-4xl mb-2 sm:mb-4">📊</div>
                    <p className="text-gray-500">No completed tasks</p>
                    <p className="text-sm text-gray-400 mt-1">Tasks will appear here with evaluations once completed</p>
                  </div>
                )}
              </div>
            </div>
              );
            })()}
          </div>
          ) : taskView === 'saved' && activeView === 'parent' ? (
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-700 mb-2 sm:mb-4">
                Saved Worksheets ({filteredSavedWorksheets.length})
              </h3>
              {filteredSavedWorksheets.length > 0 ? (
                <div className="space-y-4">
                  {filteredSavedWorksheets.map((worksheet) => (
                    <div
                      key={worksheet.id}
                      className="w-full p-4 sm:p-5 rounded-xl shadow-sm border border-gray-200 bg-gradient-to-r from-blue-50 to-white"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="flex items-center flex-wrap gap-2">
                            <h3 className="font-semibold text-gray-800 text-lm">
                              {getSavedWorksheetTitle(worksheet)}
                            </h3>
                            {worksheet.is_personalized && (
                              <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                                Personalized
                              </span>
                            )}
                            {isOlympiadWorksheet(worksheet) && (
                              <span className="text-xs px-2 py-1 rounded-full bg-orange-200 text-orange-700">
                                Olympiad
                              </span>
                            )}
                            {!isOlympiadWorksheet(worksheet) && !worksheet.is_personalized && (
                              <span className="text-xs px-2 py-1 rounded-full bg-blue-200 text-blue-700">
                                Normal
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                            <span>• Created {new Date(worksheet.created_at).toLocaleDateString()}</span>
                            {worksheet.grade && <span>• Grade {normalizeGrade(worksheet.grade)}</span>}
                            <span>• {worksheet.subject}</span>
                            <span>• {getWorksheetTopicsLabel(worksheet)}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 justify-start sm:justify-end w-full sm:w-auto">
                          <button
                            onClick={() => handleAssignSavedWorksheet(worksheet)}
                            className="px-1 py-1 sm:px-2 sm:py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-xs font-medium"
                          >
                            📋 Assign
                          </button>
                          <button
                            onClick={() => handleEditSavedWorksheet(worksheet)}
                            className="px-1 py-1 sm:px-2 sm:py-1 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 text-xs font-medium"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            onClick={() => {
                              setPendingDeleteSavedWorksheet(worksheet);
                              setShowDeleteSavedConfirm(true);
                            }}
                            className="px-1 py-1 sm:px-2 sm:py-1 bg-red-200 text-red-800 rounded-lg hover:bg-red-300 text-xs font-medium"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-purple-50">
                  <div className="text-4xl mb-2 sm:mb-4">📄</div>
                  <p className="text-gray-600">No saved worksheets yet</p>
                  <p className="text-sm text-gray-500 mt-2">Save a generated worksheet to access it here later.</p>
                </div>
              )}
            </div>
          ) : taskView === 'archive' ? (
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
              <h3 className="text-lg font-medium text-gray-700 mb-2 sm:mb-4">
                Archived Tasks ({filteredArchivedTasks.length})
              </h3>
              {filteredArchivedTasks.length > 0 ? (
                <div className="space-y-4">
                  {filteredArchivedTasks.map((taskData) => {
                    const task = taskData.task;
                    return (
                      <div key={task.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900" title={task.title}>{truncateTaskTitle(task.title)}</h3>
                            <p className="text-sm text-gray-600">
                              Created: {new Date(task.created_at).toLocaleDateString()}
                            </p>
                            <p className="text-sm text-gray-500">
                              Archived: {new Date(task.archived_at).toLocaleDateString()}
                            </p>
                          </div>
                          <button
                            onClick={() => unarchiveTask(task.id)}
                            className="text-xs px-1 py-1 sm:px-1 sm:py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                            title="Restore Task"
                          >
                            ↩️ Restore
                          </button>
                        </div>

                        {task.subject && (
                          <div className="mt-2 text-sm text-gray-500">
                            Subject: {task.subject} {task.grade && `| Grade: ${task.grade}`}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50">
                  <div className="text-4xl mb-2 sm:mb-4">💼</div>
                  <p className="text-gray-600">No archived tasks yet</p>
                </div>
              )}
            </div>
          ) : null}
          
          {showAssignSavedWorksheet && savedWorksheetToAssign && (
            <WorksheetGenerator
              onClose={() => {
                setShowAssignSavedWorksheet(false);
                setSavedWorksheetToAssign(null);
              }}
              target={activeChild}
              preSelectedSubject={savedWorksheetToAssign.subject || selectedSubject || ''}
              userRole={user?.role || 'parent'}
              preGeneratedWorksheet={savedWorksheetToAssign}
              openAssignOnLoad
              onTaskAssigned={() => {
                loadTasks();
                loadSavedWorksheets();
              }}
            />
          )}

          {showEditSavedWorksheet && savedWorksheetToEdit && (
            <EditWorksheetModal
              worksheet={savedWorksheetToEdit}
              onClose={() => {
                setShowEditSavedWorksheet(false);
                setSavedWorksheetToEdit(null);
              }}
              onWorksheetUpdated={async () => {
                await loadSavedWorksheets();
                setShowEditSavedWorksheet(false);
                setSavedWorksheetToEdit(null);
              }}
            />
          )}

          {showDeleteSavedConfirm && pendingDeleteSavedWorksheet && (
            <ConfirmModal
              title="Delete saved worksheet"
              message={`Delete "${getSavedWorksheetTitle(pendingDeleteSavedWorksheet)}"? This cannot be undone.`}
              confirmLabel="Delete"
              cancelLabel="Cancel"
              onCancel={() => {
                setShowDeleteSavedConfirm(false);
                setPendingDeleteSavedWorksheet(null);
              }}
              onConfirm={() => {
                handleDeleteSavedWorksheet(pendingDeleteSavedWorksheet);
                setShowDeleteSavedConfirm(false);
                setPendingDeleteSavedWorksheet(null);
              }}
            />
          )}

          {/* Study Support Section */}
          <div id="parent-dashboard-study-support" className="bg-white rounded-lg shadow p-4 sm:p-6 scroll-mt-24">
            <div className="min-h-[50px] max-h-[250px] overflow-y-auto">
              <StudySupportSection 
                user={user} 
                activeChild={activeChild}
                filterByRole={
                  activeView === 'parent'
                    ? 'parent'
                    : activeView === 'school'
                    ? 'school_teacher'
                    : 'coaching_teacher'
                }
                selectedSubject={selectedSubject}
                selectedGrade={parentGradeFilter}
                taskRefreshKey={taskRefreshKey}
              />
            </div>
          </div>
        </>
      )}

      {showAddChild && (
        <AddChildModal 
          onClose={() => setShowAddChild(false)}
          onChildAdded={onChildAdded}
        />
      )}
      
      {showEditChild && (
        <EditChildModal 
          child={editingChild}
          onClose={() => {
            setShowEditChild(false);
            setEditingChild(null);
          }}
          onChildUpdated={onChildAdded}
          onRequestDelete={(childToDelete) => {
            setDeletingChild(childToDelete);
            setShowDeleteConfirm(true);
          }}
        />
      )}

      {showDeleteConfirm && (
        <DeleteConfirmModal 
          child={deletingChild}
          onClose={() => {
            setShowDeleteConfirm(false);
            setDeletingChild(null);
          }}
          onConfirm={handleDeleteChild}
        />
      )}

      {showReassignConfirm && (
        <ConfirmModal
          title="Confirm reassignment"
          message="This will move the task back to pending so it can be completed again."
          confirmLabel="Reassign task"
          onCancel={() => {
            setShowReassignConfirm(false);
            setPendingReassignTaskId(null);
          }}
          onConfirm={() => {
            if (pendingReassignTaskId) {
              reassignTask(pendingReassignTaskId);
            }
            setShowReassignConfirm(false);
            setPendingReassignTaskId(null);
          }}
        />
      )}

      {showArchiveConfirm && (
        <ConfirmModal
          title="Archive task"
          message="Archived tasks can be viewed later but won't appear in your active completed tasks."
          confirmLabel="Archive task"
          onCancel={() => {
            setShowArchiveConfirm(false);
            setPendingArchiveTaskId(null);
          }}
          onConfirm={() => {
            if (pendingArchiveTaskId) {
              archiveTask(pendingArchiveTaskId);
            }
            setShowArchiveConfirm(false);
            setPendingArchiveTaskId(null);
          }}
        />
      )}

      {showDeleteTaskConfirm && pendingDeleteTask && (
        <ConfirmModal
          title="Delete task"
          message={`Delete "${pendingDeleteTask.title}"? This cannot be undone and removes it from all assigned children.`}
          confirmLabel="Delete task"
          onCancel={() => {
            setShowDeleteTaskConfirm(false);
            setPendingDeleteTask(null);
          }}
          onConfirm={() => {
            deleteTask(pendingDeleteTask.id, pendingDeleteTask.title);
            setShowDeleteTaskConfirm(false);
            setPendingDeleteTask(null);
          }}
        />
      )}

      {showViewSyllabus && currentPage !== 'view-syllabus' && (
        <ViewSyllabusModal 
          child={activeChild}
          selectedSubject={selectedSubject}
          onClose={() => setShowViewSyllabus(false)}
          currentUser={user}
          renderAsPage={currentPage === 'view-syllabus'}
          onOpenWorksheetGenerator={(config) => {
            setShowViewSyllabus(false);
            if (typeof setWorksheetGeneratorInitialConfig === 'function') {
              setWorksheetGeneratorInitialConfig(config);
            }
            setShowWorksheetGenerator(true);
          }}
        />
      )}

      {showTaskSelection && (
        <TaskSelectionModal 
          child={activeChild}
          selectedSubject={selectedSubject}
          onClose={() => setShowTaskSelection(false)}
          onSelectPracticeSheet={() => {
            setShowTaskSelection(false);
            setShowWorksheetGenerator(true);
          }}
          onSelectCreateNote={() => {
            setShowTaskSelection(false);
            openDefaultNotesGenerator();
          }}
          onSelectCreateVideo={() => {
            setShowTaskSelection(false);
            setShowVideoCreator(true);
          }}
        />
      )}

      {showMyDocs && (
        <MyDocsManagerModal
          subject={selectedSubject}
          grade={activeChild?.grade}
          childId={activeChild?.id}
          onClose={() => setShowMyDocs(false)}
        />
      )}

      {selectedTaskForUpload && (
        <SubmitWorkModal
          onClose={() => setSelectedTaskForUpload(null)}
          activeChild={activeChild}
          childSubjects={childSubjects}
          currentTask={selectedTaskForUpload}
          mode="upload"
          onWorkSubmitted={async () => {
            await loadTasks();
            setSelectedTaskForUpload(null);
          }}
        />
      )}

      {showChildInsights && currentPage !== 'insights' && (
        <ChildInsightsModal 
          child={activeChild}
          performance={childPerformance}
          pendingTasks={pendingTasks}
          completedTasks={completedTasks}
          selectedSubject={selectedSubject}
          onClose={() => setShowChildInsights(false)}
          renderAsPage={currentPage === 'insights'}
        />
      )}

      {/* Study Support Modal - All Materials */}
      {showStudySupportModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-indigo-50 via-white to-blue-50 rounded-2xl w-full max-w-4xl h-[calc(100vh-2rem)] max-h-[calc(100vh-2rem)] overflow-hidden flex flex-col shadow-2xl border border-indigo-100">
            <div className="sticky top-0 z-10 p-6 bg-gradient-to-r from-indigo-700 via-purple-700 to-blue-700 text-white border-b border-indigo-900 shadow relative">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pr-10">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-xl bg-white/20 text-white flex items-center justify-center text-2xl shadow-inner">📚</div>
                  <div>
                    <h2 className="text-xl font-semibold leading-tight">
                      Study Support for {getFirstName(activeChild?.name)}
                    </h2>
                    <p className="text-xs text-white/80">
                      Notes, worksheets, and learning resources in one place.
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowStudySupportModal(false)}
                className="text-white hover:text-white/80 transition absolute right-4 sm:right-6 top-4 sm:top-6"
              >
                ✕
              </button>
            </div>
            <div className="p-6 min-h-[50px] max-h-[250px] overflow-y-auto flex-1">
              <StudySupportSection 
                user={user} 
                activeChild={activeChild}
                filterByRole={
                  activeView === 'parent'
                    ? 'parent'
                    : activeView === 'school'
                    ? 'school_teacher'
                    : 'coaching_teacher'
                }
                selectedSubject={selectedSubject}
                selectedGrade={parentGradeFilter}
                taskRefreshKey={taskRefreshKey}
              />
            </div>
          </div>
        </div>
      )}

      {/* Parent Evaluation Modal - Detailed Insights */}
      {showParentEvaluationModal && currentParentEvaluation && (
        <FullAnalysisModal
          analysis={currentParentEvaluation}
          onClose={() => {
            setShowParentEvaluationModal(false);
            setCurrentParentEvaluation(null);
          }}
          onExplainConcept={requestConceptExplanation}
          subtitle="Edsupp insights about your child's performance"
          insightsLabel="Parent Insights"
          topSlot={
            (currentParentEvaluation?._taskInfo?.is_olympiad ||
              currentParentEvaluation?.subject ||
              currentParentEvaluation?.question_analysis?.length > 0) ? (
              <SkillWiseScoreChart evaluation={currentParentEvaluation} />
            ) : null
          }
          footer={
            <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  {/* Action Buttons for Parents/Teachers */}
                  {(() => {
                    // For parents: disable if task owner is NOT the parent (coaching/school space)
                    // For teachers: always enable
                    const isNonParentSpace = user.role === 'parent' && (
                      (currentParentEvaluation._taskInfo?.owner_id &&
                        currentParentEvaluation._taskInfo.owner_id !== user.id) ||
                      currentParentEvaluation._taskInfo?.assigned_by_role === 'school_teacher'
                    );
                    
                    console.log('[Edit Button Check - App.js]', {
                      userRole: user.role,
                      userId: user.id,
                      taskOwnerId: currentParentEvaluation._taskInfo?.owner_id,
                      isNonParentSpace
                    });
                    
                    return (
                      <>
                        <button
                          onClick={() => {
                            // Parents can only edit in parent space, teachers can edit anywhere
                            if (user.role === 'parent' && isNonParentSpace) return;
                            setShowEditEvaluationModal(true);
                          }}
                          disabled={user.role === 'parent' && isNonParentSpace}
                          className={`px-1 py-2 sm:px-2 sm:py-2 border-2 rounded-lg font-medium flex items-center text-xs sm:text-sm ${
                            (user.role === 'parent' && isNonParentSpace)
                              ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                              : 'bg-orange-600 text-white border-orange-650 hover:bg-orange-700'
                          }`}
                          title={(user.role === 'parent' && isNonParentSpace) ? 'Parents can only edit evaluations from parent space' : 'Edit evaluation scores and feedback'}
                        >
                          <span className="sm:hidden">Edit</span>
                          <span className="hidden sm:inline">✏️ Edit</span>
                          {(user.role === 'parent' && isNonParentSpace) && <span className="ml-2 text-xs">(Parent Space Only)</span>}
                        </button>
                        <button
                          onClick={() => setShowPersonalizedWorksheetModal(true)}
                          className="px-1 py-2 sm:px-2 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center text-xs sm:text-sm"
                        >
                          <span className="sm:hidden">Personalize</span>
                          <span className="hidden sm:inline">🎯 Personalize</span>
                        </button>
                        <button
                          onClick={() => openParentTargetedNotes(currentParentEvaluation)}
                          className="px-1 py-2 sm:px-2 sm:py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 font-medium flex items-center text-xs sm:text-sm"
                        >
                          <span className="sm:hidden">Notes</span>
                          <span className="hidden sm:inline">🧠 Notes</span>
                        </button>
                        <button 
                          onClick={() => handleExportReport(currentParentEvaluation)}
                          className="px-1 py-2 sm:px-2 sm:py-2 bg-purple-600 border border-gray-900 text-white rounded-lg hover:bg-purple-700 text-xs sm:text-sm flex items-center"
                        >
                          <span className="sm:hidden">Save</span>
                          <span className="hidden sm:inline">💾 Save</span>
                        </button>
                        <button
                          onClick={() => {
                            setShowParentEvaluationModal(false);
                            setCurrentParentEvaluation(null);
                          }}
                          className="px-1 py-2 sm:px-2 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs sm:text-sm"
                        >
                          <span className="sm:hidden">Close</span>
                          <span className="hidden sm:inline">Close</span>
                        </button>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          }
        />
      )}

      {/* Edit Evaluation Modal */}
      {showEditEvaluationModal && currentParentEvaluation && (
        <EditEvaluationModal
          evaluation={currentParentEvaluation}
          onClose={() => setShowEditEvaluationModal(false)}
          onUpdate={(updatedEvaluation) => {
            // Update the current evaluation with the edited version
            setCurrentParentEvaluation(updatedEvaluation);
            // Reload evaluations to refresh the list
            if (activeChild) {
              loadChildEvaluations(activeChild.id);
            }
            setShowEditEvaluationModal(false);
          }}
        />
      )}

      {/* Personalized Worksheet Modal */}
      {showPersonalizedWorksheetModal && currentParentEvaluation && (
        <PersonalizedWorksheetModal
          evaluation={currentParentEvaluation}
          userRole={user?.role || 'parent'}
          childrenList={children || []}
          assignmentTargets={{ students: [], batches: [] }}
          onClose={() => setShowPersonalizedWorksheetModal(false)}
          onWorksheetGenerated={(worksheet, analysis) => {
            setShowPersonalizedWorksheetModal(false);
            // Refresh the page or show success
          }}
        />
      )}

      {/* Answer Key Modal */}
      {showAnswerKeyModal && currentAnswerKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">Answer Key</h2>
                <button
                  onClick={() => {
                    setShowAnswerKeyModal(false);
                    setCurrentAnswerKey(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">
                {currentAnswerKey.worksheet?.subject || currentAnswerKey.task?.subject || 'N/A'} • {currentAnswerKey.worksheet?.topics && Array.isArray(currentAnswerKey.worksheet.topics) ? currentAnswerKey.worksheet.topics.join(', ') : 'All Topics'}
              </p>
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              {/* Answer Key Only - No Questions */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2 sm:mb-4">📝 Answer Key</h3>
                <KaTeXContent
                  content={currentAnswerKey.answer_key}
                  className="border rounded-lg p-4 bg-green-50 answer-key-content"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Answer Comparison Modal */}
      {showComparisonModal && currentComparison && (
        <CompareAnswersModal
          comparisonData={currentComparison}
          onClose={() => {
            setShowComparisonModal(false);
            setCurrentComparison(null);
          }}
          onExplainConcept={requestConceptExplanation}
          title="Answer Comparison"
          studentAnswerLabel="Child Answer"
          emptyState={
                <div className="text-center py-8">
                  <div className="text-4xl mb-2 sm:mb-4">📋</div>
                  <p className="text-gray-500 text-lg font-medium">No detailed question analysis available</p>
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg max-w-md mx-auto">
                    <p className="text-sm text-blue-700">
                      <strong>This could be because:</strong>
                    </p>
                    <ul className="text-sm text-blue-600 mt-2 space-y-1">
                      <li>• Evaluation is still in progress</li>
                      <li>• The task was submitted as general work (not structured questions)</li>
                      <li>• The evaluation system is processing the answers</li>
                    </ul>
                    <p className="text-xs text-blue-500 mt-3">
                      Try refreshing in a few minutes if the task was recently completed.
                    </p>
                  </div>
                </div>
          }
          footer={
            currentComparison._evaluation && user && (user.role === 'parent' || ['coaching_teacher', 'school_teacher'].includes(user.role)) && (
              <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-3">
                    {/* Action Buttons for Parents/Teachers */}
                    {(() => {
                      // For parents: disable if task owner is NOT the parent (coaching/school space)
                      // For teachers: always enable
                      const isNonParentSpace = user.role === 'parent' && (
                        (currentComparison._taskInfo?.owner_id &&
                          currentComparison._taskInfo.owner_id !== user.id) ||
                        currentComparison._taskInfo?.assigned_by_role === 'school_teacher'
                      );
                      
                      console.log('[Edit Button Check - Comparison Modal]', {
                        userRole: user.role,
                        userId: user.id,
                        taskOwnerId: currentComparison._taskInfo?.owner_id,
                        isNonParentSpace
                      });
                      
                      return (
                        <>
                          <button
                            onClick={() => {
                              // Parents can only edit in parent space, teachers can edit anywhere
                              if (user.role === 'parent' && isNonParentSpace) return;
                              setShowEditEvaluationModal(true);
                            }}
                            disabled={user.role === 'parent' && isNonParentSpace}
                            className={`px-2 py-2 border-2 rounded-lg font-medium flex items-center text-sm ${
                              (user.role === 'parent' && isNonParentSpace)
                                ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                                : 'bg-orange-600 text-white border-orange-650 hover:bg-orange-700'
                            }`}
                            title={(user.role === 'parent' && isNonParentSpace) ? 'Parents can only edit evaluations from parent space' : 'Edit evaluation scores and feedback'}
                          >
                            <span className="sm:hidden">Edit</span>
                            <span className="hidden sm:inline">✏️ Edit</span>
                            {(user.role === 'parent' && isNonParentSpace) && <span className="ml-2 text-xs">(Parent Space Only)</span>}
                          </button>
                          <button
                            onClick={() => setShowPersonalizedWorksheetModal(true)}
                            className="px-2 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium flex items-center text-sm"
                          >
                            <span className="sm:hidden">Personalize</span>
                            <span className="hidden sm:inline">🎯 Personalize</span>
                          </button>
                          <button
                            onClick={() => openParentTargetedNotes(currentComparison._evaluation)}
                            className="px-2 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 font-medium flex items-center text-sm"
                          >
                            <span className="sm:hidden">Notes</span>
                            <span className="hidden sm:inline">🧠 Notes</span>
                          </button>
                        </>
                      );
                    })()}
                  </div>
                  <button
                    onClick={() => {
                      setShowComparisonModal(false);
                      setCurrentComparison(null);
                    }}
                    className="px-2 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
            )
          }
        />
      )}


      {/* Edit Evaluation Modal from Comparison */}
      {showEditEvaluationModal && currentComparison && currentComparison._evaluation && (
        <EditEvaluationModal
          evaluation={currentComparison._evaluation}
          onClose={() => setShowEditEvaluationModal(false)}
          onUpdate={(updatedEvaluation) => {
            // Update the evaluation in comparison
            setCurrentComparison({
              ...currentComparison,
              _evaluation: updatedEvaluation
            });
            setShowEditEvaluationModal(false);
          }}
        />
      )}

      {/* Personalized Worksheet Modal from Comparison */}
      {showPersonalizedWorksheetModal && currentComparison && currentComparison._evaluation && (
        <PersonalizedWorksheetModal
          evaluation={currentComparison._evaluation}
          userRole={user?.role || 'parent'}
          childrenList={children || []}
          assignmentTargets={{ students: [], batches: [] }}
          onClose={() => setShowPersonalizedWorksheetModal(false)}
          onWorksheetGenerated={(worksheet, analysis) => {
            setShowPersonalizedWorksheetModal(false);
          }}
        />
      )}

      {/* Practice Sheet Viewing Modal */}
      {showPracticeSheetModal && currentPracticeSheet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-10 border-b border-gray-200 relative">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{truncateTaskTitle(currentPracticeSheet.task.title)}</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Subject: {currentPracticeSheet.worksheet?.subject || currentPracticeSheet.task?.subject || 'N/A'} • Topics: {getWorksheetTopicsLabel(currentPracticeSheet.worksheet)}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:justify-end">
                  {currentPracticeSheet?.worksheet?.answer_key && (
                    <button
                      onClick={togglePracticeSheetAnswerKey}
                      className="px-1 py-1 sm:px-2 sm:py-2 border border-amber-800 bg-amber-700 text-white rounded-lg hover:bg-amber-800 flex items-center space-x-1 text-xs sm:text-sm"
                    >
                      <span>🔑</span>
                      <span>{showPracticeSheetAnswerKey ? 'Hide' : 'View'} Answer Key</span>
                    </button>
                  )}
                  <button
                    onClick={printPracticeSheet}
                    className="px-1 py-1 sm:px-2 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-800 flex items-center space-x-2 text-xs sm:text-sm"
                  >
                    <span>🖨️</span>
                    <span>Print</span>
                  </button>
                  <button
                    onClick={downloadPDF}
                    className="px-1 py-1 sm:px-2 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-1 text-xs sm:text-sm"
                  >
                    <span>💾</span>
                    <span>PDF</span>
                  </button>
                </div>
              </div>
              <button
                onClick={closePracticeSheet}
                className="absolute right-4 top-4 text-xl text-gray-400 hover:text-gray-600"
                aria-label="Close practice sheet"
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              {currentPracticeSheet.worksheet?.is_general_evaluation && currentPracticeSheet.worksheet?.uploaded_files?.length > 0 ? (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Original Uploaded Document</h3>
                  {currentPracticeSheet.worksheet.uploaded_files.map((file, index) => (
                    <div key={index} className="border rounded-lg overflow-hidden">
                                            {!file.content_base64 ? (
                        <div className="p-4 text-center text-gray-600">
                          <p>File: {file.filename}</p>
                          <p className="text-sm">This document's content is no longer available to preview.</p>
                        </div>
                      ) : file.content_type?.startsWith('image/') ? (
                        <img
                          src={`data:${file.content_type};base64,${file.content_base64}`}
                          alt={file.filename}
                          className="w-full h-auto"
                        />
                      ) : file.content_type === 'application/pdf' ? (
                        <div className="p-4 text-center">
                          <p className="text-gray-600 mb-2 sm:mb-4">PDF File: {file.filename}</p>
                          <iframe
                            src={`data:application/pdf;base64,${file.content_base64}`}
                            className="w-full"
                            style={{ minHeight: '600px' }}
                            title={file.filename}
                          />
                        </div>
                      ) : (
                        <div className="p-4 text-center text-gray-600">
                          <p>File: {file.filename}</p>
                          <p className="text-sm">Preview not available for this file type</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : currentPracticeSheet.worksheet && currentPracticeSheet.worksheet.html_content ? (
                <EnhancedWorksheetDisplay
                  worksheet={currentPracticeSheet.worksheet}
                  showAnswerKey={showPracticeSheetAnswerKey}
                  className="grid grid-cols-1 gap-4"
                  onWorksheetUpdated={updateCurrentPracticeSheetWorksheet}
                  canEditDiagram={
                    currentPracticeSheet?.task?.owner_id === user?.id ||
                    currentPracticeSheet?.worksheet?.creator_id === user?.id
                  }
                  canEditQuestions={
                    currentPracticeSheet?.task?.owner_id === user?.id ||
                    currentPracticeSheet?.worksheet?.creator_id === user?.id
                  }
                />
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <p>No worksheet content available</p>
                  {currentPracticeSheet.worksheet?.is_general_evaluation && (
                    <p className="text-sm mt-2">This is a general evaluation from an external upload.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />
      
      {/* AI Chat Assistant */}
      <AIChatAssistant 
        user={user} 
        childId={activeChild?.id}
        evaluationId={currentParentEvaluation?.id || null}
        taskId={currentParentEvaluation?.task_id || null}
        externalRequest={aiChatRequest}
        isSuppressed={shouldHideChatAssistant}
      />

    </div>
  );
};

// Task Card Component for Coaching Tasks
function App() {
  const [alertDialog, setAlertDialog] = useState(null);

  // Initialize Capacitor for mobile app
  useEffect(() => {
    initializeCapacitor();
    return () => cleanupCapacitor();
  }, []);

  useEffect(() => {
    const originalAlert = window.alert;
    window.alert = (message) => {
      setAlertDialog({
        message: typeof message === 'string' ? message : String(message),
      });
    };

    return () => {
      window.alert = originalAlert;
    };
  }, []);

  return (
    <AuthProvider>
      <ConfirmProvider>
        <Router>
          <div className="App app-gradient">
            <ImpersonationBanner />
            <Suspense fallback={<div className="flex items-center justify-center min-h-screen text-gray-500">Loading…</div>}>
            <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/guest/assignment/:token" element={<GuestAssignmentPage />} />
            {/* Coach micro-site — rendered SPA-side off /api/public/coach/<slug>.
                Backend still serves the Jinja version at the same path for
                bots/SEO; this React route catches the link in deployments
                where FRONTEND_URL points at the SPA, so visitors don't get
                bounced to the landing page by the catch-all below. */}
            <Route path="/coach/:slug" element={<PublicCoachPage />} />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <RoleBasedRedirect>
                    <Dashboard 
                      TeacherDashboard={TeacherDashboard}
                      ParentDashboard={ParentDashboard}
                      ChildDashboard={ChildDashboard}
                      CoachingChildView={CoachingChildView}
                      WorksheetGenerator={WorksheetGenerator}
                      NotesGenerator={NotesGenerator}
                      NotesAssignmentView={NotesAssignmentView}
                      ShareNotesModal={ShareNotesModal}
                      SubmitWorkModal={SubmitWorkModal}
                      ParentAccessCodeModal={ParentAccessCodeModal}
                      ChildAccessCodeModal={ChildAccessCodeModal}
                      SuperAdminDashboard={SuperAdminDashboard}
                      SchoolAdminDashboard={SchoolAdminDashboard}
                      CoachingTasksSection={CoachingTasksSection}
                      CoachingStudentsSection={CoachingStudentsSection}
                      StudySupportSection={StudySupportSection}
                      EnhancedWorksheetDisplay={EnhancedWorksheetDisplay}
                    />
                  </RoleBasedRedirect>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/child-dashboard" 
              element={
                <ProtectedRoute>
                  <ChildDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/question-bank" 
              element={
                <ProtectedRoute>
                  <QuestionBankDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/billing" 
              element={
                <ProtectedRoute>
                  <SubscriptionScreen />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/billing/success" 
              element={
                <ProtectedRoute>
                  <div className="flex items-center justify-center min-h-screen">
                    <div className="text-center">
                      <div className="text-6xl mb-2 sm:mb-4">🎉</div>
                      <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
                      <p className="text-gray-600 mb-6">Your subscription has been activated.</p>
                      <a href="/billing" className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                        View Subscription
                      </a>
                    </div>
                  </div>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/billing" 
              element={
                <ProtectedRoute>
                  <AdminBillingDashboard />
                </ProtectedRoute>
              } 
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
            </Suspense>
            {alertDialog && (
              <ConfirmModal
                title="Notice"
                message={alertDialog.message}
                confirmLabel="OK"
                showCancel={false}
                onConfirm={() => setAlertDialog(null)}
                onCancel={() => setAlertDialog(null)}
              />
            )}
          </div>
        </Router>
      </ConfirmProvider>
    </AuthProvider>
  );
}

export default App;